import {
  ArrowUp,
  CirclePlus,
  // LucideArrowDown,
  LucideArrowUp,
  LucideChevronDown,
  LucideEye,
  LucideExternalLink,
  LucideFileText,
  // LucideCheck,
  // LucideChevronDown,
  LucideInfo,
  LucidePackage,
  LucideShoppingCart,
  LucideTrash2,
  LucideUpload,
  LucideX,
  Search,
} from 'lucide-react';
import React, { ChangeEvent, Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { SEARCH_INPUT_DEBOUNCE_MS, useDebounce, useIsMobile } from '../helper/customHooks';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { InputGroup, InputGroupAddon, InputGroupInput } from '../components/UI/input-group';
import ColumnVisibilityMenu from '../components/orderingAndReceiving/column_visibility_menu';
import {
  DEFAULT_ORDERING_COLUMN_VISIBILITY,
  ORDERING_COLUMNS,
  isColumnVisible,
  OrderingColumnKey,
  OrderingColumnVisibility,
  resolveColumnVisibility,
} from '../components/orderingAndReceiving/column_visibility';
import {
  CUSTOM_COLUMN_VISIBILITY_KEY_BY_ID,
  isConfiguredCustomColumn,
  normalizeCustomColumnsConfig,
  readCustomColumnValue,
  writeCustomColumnValue,
  type CustomColumnId,
} from '../components/orderingAndReceiving/custom_columns';
import CustomColumnTextCell from '../components/orderingAndReceiving/custom_column_text_cell';
import {
  CustomColumnPreference,
  getColumnVisibilityPreference,
  saveCustomColumnsPreference,
  upsertColumnVisibilityPreference,
} from '../api/column-visibility-preferences';
import {
  getAllInfo,
  getClientOrderingAndReceivingComponents,
  updateClientOrderingAndReceivingTable,
} from '../api/drapery-order';
import { getAll as getAllCategories } from '../api/category';
import { cn } from '../lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import UploadErrorBanner from '../components/UI/upload-error-banner';
import { Tooltip, TooltipContent, TooltipTrigger } from '../components/ui/tooltip-shadcn';
// import { IoIosArrowRoundUp } from 'react-icons/io';
import { getAllClients } from '../api/client';
import Filter from '../components/filter/Filter';
import { getSuppliers } from '../api/supplier';
import { FadeLoader } from 'react-spinners';
import moment from 'moment';
import { Calendar } from '../components/UI/calendar';
import { Input } from '../components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/UI/dropdown-menu';
import SupplierModal from '../containers/supplier/supplier_modal';
import { SuppliersModule, WorkOrderModule } from '../redux';
import { ScrollToPosition } from '../containers/application';
import KravetPlaceholderImage from '../assets/images/kravet-image-placeholder.png';
import { editWorkOrder, getWorkOrderPDFContent } from '../api/work-order';
import { callLambda, getPdfFileNameFromLambdaResponse } from '../api/lambda';
import {
  generatePresignedUrl,
  isStoredFileMissingError,
  openStoredPdf,
  storeFile,
} from '../api/file-store';
import { Config } from '../utils';
import { isAbsoluteImageSrc, resolveImageSrc } from '../utils/image-src';
import {
  addDraperyOrderUpload,
  deleteDraperyOrderUpload,
  editDraperyOrderUpload,
} from '../api/drapery-order-upload';
import { uploadImage } from '../api/account';
import { toast } from 'sonner';
import { regenerateWorkOrderDrawing } from '../helper/regenerate-work-order-drawing';
import { initial_statuses } from '../initials/initials';
import { getAllStatuses } from '../api/statuses';
import LoadingModal from '../modals/loading-modal';
import PaginationEndIcon from '../assets/icons/chevron-end.svg';
import PaginationNextIcon from '../assets/icons/chevron-right.svg';
import { normalizeUrl } from '../helper/helpers';
import {
  COLUMN_VISIBILITY_PAGES,
  normalizeColumnVisibilityPreference,
} from '../helper/columnVisibilityPreference';
import StandardItemModal from '../components/my-shop/standard-item-modal/main';
import Notebook from '../components/notebook/Notebook';

type OrderingAndReceivingProps = {
  account?: any;
  accountUser?: any;
  canAccessTeamSettings?: boolean;
  history?: any;
  handleOpenSinglePurchasedGoods?(val: number | null): void;
  backToTop?: any;
  pageScrollTo?(position: ScrollToPosition, val: number): void;
  clientScopeId?: number;
  hiddenColumnKeys?: OrderingColumnKey[];
  showPageHeader?: boolean;
  handleLinkedPO(val: string, type: string): void;
  canOpenPO?: boolean;
};

export const ALLOWED_IMAGE_EXTENSIONS = ['jpeg', 'jpg', 'png', 'svg', 'webp'];
export const MAX_FILE_SIZE_KB = 10240;
export const PDF_CONTENT_TYPE = 'application/pdf';
export const PDF_UPLOAD_ERROR_MESSAGE = 'Failed to upload PDF. Please try again.';

const createInitialFilters = (includeClientFilter: boolean) => {
  const filters: any[] = [];

  if (includeClientFilter) {
    filters.push({ id: 1, type: 'single', label: 'Client', value: 'client', items: [] });
  }

  filters.push(
    {
      id: includeClientFilter ? 2 : 1,
      type: 'multi',
      label: 'Supplier',
      value: 'supplier',
      items: [],
    },
    // todo: uncomment when needed
    // {
    //   id: includeClientFilter ? 3 : 2,
    //   type: 'multi',
    //   label: 'Type',
    //   value: 'type',
    //   items: [],
    // },
    {
      id: includeClientFilter ? 4 : 3,
      type: 'single',
      label: 'Purchaser',
      value: 'purchasers',
      items: [
        { id: 1, label: 'COM', value: 'com' },
        { id: 2, label: 'Workroom', value: 'workroom' },
      ],
    },
    {
      id: includeClientFilter ? 5 : 4,
      type: 'single',
      label: 'Ordering Status',
      value: 'orderingStatus',
      items: [
        { id: 1, label: 'Not Ordered', value: 'not-ordered' },
        { id: 2, label: 'Ordered', value: 'ordered' },
        { id: 3, label: 'Received', value: 'received' },
      ],
    },
    {
      id: includeClientFilter ? 6 : 5,
      type: 'multi',
      label: 'Item Status',
      value: 'itemStatus',
      items: [],
    },
    {
      id: includeClientFilter ? 7 : 6,
      type: 'multi',
      label: 'Invoice',
      value: 'invoice',
      items: [
        { id: 1, label: 'Not Created', value: 'Not Created' },
        { id: 2, label: 'Not Paid', value: 'Not Paid' },
        { id: 3, label: 'Partial', value: 'Partial' },
        { id: 4, label: 'Paid', value: 'Paid' },
      ],
    }
  );

  return filters;
};

const initialFilterState = {
  client: { value: [] },
  supplier: { value: [] },
  type: { value: [] },
  purchasers: { value: [] },
  orderingStatus: { value: [] },
  itemStatus: { value: [] },
  invoice: { value: [] },
};

const initialSortField = 'dueDate';
const ORDERING_COLUMN_KEYS = ORDERING_COLUMNS.map((column) => column.key);
const paginationSelectClassName =
  'h-9 rounded-md border border-input bg-white px-3 pr-8 text-sm shadow-xs focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]';

const uploadSuccessToast = (item: any) => {
  if (item === 'purchased-goods') {
    return {
      title: 'PO document uploaded',
      description: 'The PO document has been uploaded successfully.',
    };
  } else {
    return {
      title: 'File uploaded',
      description: 'The file has been uploaded successfully.',
    };
  }
};

// const invoiceSortRank: Record<string, number> = {
//   'not created': 0,
//   'not paid': 1,
//   partial: 2,
//   paid: 3,
// };

// const normalizeText = (value: any): string =>
//   value === undefined || value === null ? '' : String(value).trim().toLowerCase();

const toOptionalPositiveInt = (value: any): number | undefined => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
};

const getRowSummaryComponent = (row: any) => {
  if (row?.source === 'drapery-summary-component') {
    return row?.component || null;
  }
  return row?.component?.draperyOrderSummaryComponent || null;
};

const getRowSelectionKey = (row: any, fallbackIndex?: number): string => {
  const summaryId = getRowSummaryComponent(row)?.id;
  const componentId = row?.component?.id;
  const componentIndex = row?.componentIndex;
  return [
    row?.order?.id ?? 'order',
    row?.source ?? 'source',
    summaryId ?? componentId ?? componentIndex ?? fallbackIndex ?? 'row',
  ].join(':');
};

const getNotebookComponentId = (row: any): number | null => {
  const contextComponentId = Number(row?.notebookComponentContext?.componentId || 0);
  if (Number.isInteger(contextComponentId) && contextComponentId > 0) return contextComponentId;

  const explicitComponentId = Number(row?.componentId || row?.summaryComponentId || 0);
  if (Number.isInteger(explicitComponentId) && explicitComponentId > 0) return explicitComponentId;

  const sourceComponentId = Number(row?.sourceComponentId || row?.component?.id || 0);
  if (Number.isInteger(sourceComponentId) && sourceComponentId > 0) return sourceComponentId;

  const summaryComponentId = Number(getRowSummaryComponent(row)?.id || 0);
  if (Number.isInteger(summaryComponentId) && summaryComponentId > 0) return summaryComponentId;

  return null;
};

type NotebookComponentIdType =
  | 'summary_component'
  | 'fabric'
  | 'pg_build_component'
  | 'pg_total_only';

const getNotebookComponentIdType = (row: any): NotebookComponentIdType | null => {
  const contextType = String(row?.notebookComponentContext?.componentIdType || '').trim();
  if (
    contextType === 'summary_component' ||
    contextType === 'fabric' ||
    contextType === 'pg_build_component' ||
    contextType === 'pg_total_only'
  ) {
    return contextType;
  }

  const explicitType = String(row?.componentIdType || '').trim();
  if (
    explicitType === 'summary_component' ||
    explicitType === 'fabric' ||
    explicitType === 'pg_build_component' ||
    explicitType === 'pg_total_only'
  ) {
    return explicitType;
  }

  const summaryComponentId = Number(getRowSummaryComponent(row)?.id || 0);
  if (Number.isInteger(summaryComponentId) && summaryComponentId > 0) return 'summary_component';

  const sourceComponentId = Number(row?.sourceComponentId || row?.component?.id || 0);
  if (!Number.isInteger(sourceComponentId) || sourceComponentId <= 0) return null;

  if (row?.source === 'drapery-fabric') return 'fabric';
  if (row?.source === 'drapery-summary-component') return 'summary_component';

  return null;
};

const getNotebookComponentKey = (row: any): string | null => {
  const contextKey = String(row?.notebookComponentContext?.componentKey || '').trim();
  if (contextKey) return contextKey;

  const explicitKey = String(row?.componentKey || '').trim();
  if (explicitKey) return explicitKey;

  return null;
};

const hasNotebookComponentScope = (row: any): boolean => {
  const type = getNotebookComponentIdType(row);
  if (!type) return false;
  const id = getNotebookComponentId(row);
  const key = getNotebookComponentKey(row);
  return Boolean((id && id > 0) || key);
};

const clearNotebookDeepLinkSearchParams = () => {
  if (typeof window === 'undefined') return;

  const url = new URL(window.location.href);
  const keys = [
    'openNotebook',
    'noteId',
    'itemId',
    'projectId',
    'notebookTable',
    'tabType',
    'componentId',
    'componentIdType',
    'componentKey',
  ];

  let changed = false;
  keys.forEach((key) => {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key);
      changed = true;
    }
  });

  if (changed) {
    const nextUrl = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState({}, '', nextUrl);
  }
};

const DISPLAY_TEXT_CANDIDATE_KEYS = [
  'name',
  'item',
  'label',
  'displayName',
  'title',
  'value',
  'estimateDescription',
  'orderingDescription',
];

const normalizeCategoryDisplayText = (value: any, depth = 0): string => {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value).trim();
  }

  if (depth >= 2 || typeof value !== 'object') {
    return '';
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const normalizedItem = normalizeCategoryDisplayText(item, depth + 1);
      if (normalizedItem) return normalizedItem;
    }
    return '';
  }

  for (const key of DISPLAY_TEXT_CANDIDATE_KEYS) {
    const normalizedCandidate = normalizeCategoryDisplayText(value?.[key], depth + 1);
    if (normalizedCandidate) return normalizedCandidate;
  }

  return '';
};

const isCustomPartsAndSuppliesSummaryRow = (row: any): boolean => {
  const item = normalizeCategoryDisplayText(
    row?.component?.draperyOrderSummaryComponent?.item || row?.component?.item
  ).toUpperCase();
  const isStaticHardware = Boolean(
    row?.component?.draperyOrderSummaryComponent?.isStaticHardware ||
    row?.component?.isStaticHardware
  );

  return (
    row?.order?.orderType === 'custom-order' &&
    row?.source === 'drapery-summary-component' &&
    isStaticHardware &&
    ['HARDWARE', 'PARTS & SUPPLIES'].includes(item)
  );
};

const getPartsAndSuppliesCategoryOverride = (row: any) => {
  if (!isCustomPartsAndSuppliesSummaryRow(row)) return null;
  const itemName = normalizeCategoryDisplayText(
    row?.hardware?.hardwareItem || row?.order?.draperyOrderHardware?.hardwareItem
  );
  return itemName ? { name: itemName, preserveCase: true } : null;
};

// const getSupplierItem = (row: any) => {
//   if (['drapery-order', 'roman-shade-order', 'custom-order']?.includes(row?.order?.orderType)) {
//     const patternObj = row?.component?.patternObj || null;
//     switch (row?.source) {
//       case 'drapery-fabric':
//         if (isNaN(row?.componentType)) {
//           return row?.hardware?.supplierObj || row?.headrail?.supplierObj || null;
//         }
//         return (
//           row?.component?.supplier ||
//           (patternObj?.supplierId
//             ? { id: patternObj.supplierId, displayName: patternObj.supplierName }
//             : null)
//         );
//       case 'drapery-summary-component':
//         return row?.hardware?.supplierObj || row?.headrail?.supplierObj || null;
//       default:
//         return null;
//     }
//   }
//   if (row?.order?.orderType === 'purchased-goods') {
//     return row?.order?.purchasedGoodsSupplier || null;
//   }
//   return null;
// };

const getPurchaserDisplayName = (row: any) => {
  if (['drapery-order', 'roman-shade-order', 'custom-order']?.includes(row?.order?.orderType)) {
    let orderOrigin = 0;
    switch (row?.source) {
      case 'drapery-fabric':
        orderOrigin =
          row?.component?.orderOrigin || row?.hardware?.purchaser || row?.headrail?.purchaser || 0;
        break;
      case 'drapery-summary-component':
        orderOrigin = row?.hardware?.purchaser || row?.headrail?.purchaser || 0;
        break;
      default:
        orderOrigin = 0;
        break;
    }
    return orderOrigin === 2 ? 'COM' : orderOrigin === 1 ? 'Workroom' : '-';
  }
  if (row?.order?.orderType === 'purchased-goods') {
    const pgOrderOrigin = row?.order?.purchasedGoodsPurchaser ?? 0;
    return pgOrderOrigin === 2 ? 'COM' : pgOrderOrigin === 1 ? 'Workroom' : '-';
  }
  return '-';
};

// const getItemDisplayName = (row: any) =>
//   row?.order?.item?.name ||
//   row?.order?.itemType?.name ||
//   row?.order?.purchasedGoodsItemType?.name ||
//   'Drapery Order';

// const getRoomDisplayName = (row: any) => row?.order?.room?.name || row?.order?.roomName || '';

// const getClientDisplayName = (row: any) =>
//   row?.order?.client?.displayName ||
//   `${row?.order?.client?.firstName || ''} ${row?.order?.client?.lastName || ''}`.trim() ||
//   '-';

// const getParentClientDisplayName = (row: any) =>
//   row?.order?.client?.parentClient?.displayName ?? null;

// const getInvoiceData = (row: any) => {
//   const invoiceJson = row?.order?.draperyOrderSummary?.invoiceJson || null;
//   return {
//     status: invoiceJson?.paid || null,
//     number: invoiceJson?.num || null,
//     date: invoiceJson?.invoiceDateSent || null,
//   };
// };

// const normalizeCategoryToken = (value: any): string =>
//   value === undefined || value === null
//     ? ''
//     : String(value)
//         .trim()
//         .toLowerCase()
//         .replace(/[^a-z0-9]+/g, ' ')
//         .trim();

// const resolveSpecialCategoryLabel = (row: any): string => {
//   const orderType = row?.order?.orderType || '';
//   const numericComponentType = Number(row?.componentType);
//   const hasNumericComponentType = Number.isFinite(numericComponentType);
//   const candidates = [
//     row?.component?.draperyOrderSummaryComponent?.item,
//     row?.component?.item,
//     row?.component?.name,
//     row?.component?.type,
//   ];

//   for (const candidate of candidates) {
//     const token = normalizeCategoryToken(candidate);
//     if (!token) continue;
//     if (token.includes('part') && token.includes('suppl')) return 'Parts & Supplies';
//     if (token.includes('headrail')) return 'Headrail';
//     if (token.includes('hardware')) {
//       return orderType === 'custom-order' ? 'Parts & Supplies' : 'Hardware';
//     }
//   }

//   if (row?.source === 'drapery-fabric' && hasNumericComponentType && numericComponentType === 4) {
//     if (orderType === 'roman-shade-order') return 'Headrail';
//     if (orderType === 'custom-order') return 'Parts & Supplies';
//     return 'Hardware';
//   }

//   const isSummaryLike =
//     row?.source === 'drapery-summary-component' ||
//     isNaN(row?.componentType) ||
//     (hasNumericComponentType && numericComponentType === 4);
//   if (!isSummaryLike) {
//     return '';
//   }

//   if (orderType === 'roman-shade-order' && row?.headrail) return 'Headrail';
//   if (orderType === 'custom-order' && row?.hardware) return 'Parts & Supplies';
//   if (row?.hardware) return 'Hardware';
//   if (row?.headrail) return 'Headrail';

//   return '';
// };

const getCategoryData = (row: any) => {
  const patternObj = row?.component?.patternObj || row?.component?.item || null;
  // const specialCategoryLabel = resolveSpecialCategoryLabel(row);
  const partsAndSuppliesCategory = getPartsAndSuppliesCategoryOverride(row);
  const category =
    partsAndSuppliesCategory ||
    // (specialCategoryLabel ? { name: specialCategoryLabel } : null) ||
    patternObj?.category ||
    row?.order?.purchasedGoodsItemType ||
    row?.component?.draperyOrderSummaryComponent ||
    row?.component ||
    null;
  const subcategory =
    /*specialCategoryLabel
    ? null
    : */ row?.order?.orderType === 'purchased-goods'
      ? row?.order?.purchasedGoodsComponentsType === 'components-build'
        ? row?.component?.category || null
        : row?.order?.purchasedGoodsItemType?.parentCategory || null
      : patternObj?.subcategory || null;
  return { category, subcategory, patternObj };
};

// const getOrderedDateValue = (row: any) =>
//   row?.component?.orderedDate || getRowSummaryComponent(row)?.orderedDate || undefined;

// const getReceivedDateValue = (row: any) =>
//   row?.component?.receivedDate || getRowSummaryComponent(row)?.receivedDate || undefined;

// const getEtaDateValue = (row: any) =>
//   row?.component?.eta || getRowSummaryComponent(row)?.eta || undefined;

// const getInstallationDateValue = (row: any) =>
//   row?.order?.draperyOrderSummary?.installationJson?.dateOfInstall || null;

// const getDueDateValue = (row: any) =>
//   row?.order?.draperyOrderSummary?.productionJson?.dueDate || null;

// const getRowQtyValue = (row: any): { value: any; isYards: boolean } => {
//   const yardsNeeded = row?.component?.yardsNeeded;
//   if (yardsNeeded !== undefined && yardsNeeded !== null && yardsNeeded !== '') {
//     return { value: yardsNeeded, isYards: true };
//   }

//   const yardsRecommended = row?.component?.yardsRecommended;
//   if (yardsRecommended !== undefined && yardsRecommended !== null && yardsRecommended !== '') {
//     return { value: yardsRecommended, isYards: true };
//   }

//   return {
//     value:
//       row?.component?.quantity ??
//       row?.component?.qty ??
//       getRowSummaryComponent(row)?.quantity ??
//       getRowSummaryComponent(row)?.qty ??
//       row?.hardware?.hardwareQuantity ??
//       row?.headrail?.quantity,
//     isYards: false,
//   };
// };

// const formatQtyDisplay = (row: any) => {
//   const { value, isYards } = getRowQtyValue(row);
//   if (value === undefined || value === null || value === '') return '-';
//   return isYards ? `${value} yards` : `${value}`;
// };

// const getQtyNumericValue = (row: any) => {
//   const { value: raw } = getRowQtyValue(row);
//   const normalized = Number(raw);
//   return Number.isFinite(normalized) ? normalized : normalizeText(raw);
// };

// const dateSortValue = (value: any) => {
//   if (!value) return Number.POSITIVE_INFINITY;
//   const timestamp = new Date(value).getTime();
//   return Number.isNaN(timestamp) ? Number.POSITIVE_INFINITY : timestamp;
// };

// const compareValues = (left: any, right: any) => {
//   if (typeof left === 'number' && typeof right === 'number') {
//     if (left === right) return 0;
//     return left < right ? -1 : 1;
//   }
//   return normalizeText(left).localeCompare(normalizeText(right), undefined, {
//     numeric: true,
//     sensitivity: 'base',
//   });
// };

const ClientOrderingAndReceiving = (props: OrderingAndReceivingProps) => {
  const locationSearch =
    props.history?.location?.search ||
    (typeof window !== 'undefined' ? window.location.search : '');
  const { account, clientScopeId, hiddenColumnKeys = [], showPageHeader = true } = props;
  const includeClientFilter = !clientScopeId;
  const canCustomizeCustomColumns = !!account?.isOwner || !!account?.isAdmin;
  const baseHiddenColumnKeys = useMemo<OrderingColumnKey[]>(
    () => [
      ...hiddenColumnKeys,
      ...(clientScopeId ? (['clientProject'] as OrderingColumnKey[]) : []),
    ],
    [clientScopeId, hiddenColumnKeys]
  );

  const accountId =
    account?.parentAccountId ||
    account?.id ||
    parseFloat(localStorage.getItem('ownerAccountId') || '0') ||
    parseFloat(localStorage.getItem('accountId') || '0');

  const searchInputRef = useRef<any>();
  const headerWrapperRef = useRef<HTMLDivElement | null>(null);
  const bodyWrapperRef = useRef<HTMLDivElement | null>(null);
  const syncingHorizontalScroll = useRef(false);

  const isMobile = useIsMobile(800);

  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [rowIndex, setRowIndex] = useState<number | null>(null);
  const [uploadIndex, setUploadIndex] = useState<number | null>(null);
  const [poNumberState, setPoNumberState] = useState<string>('');
  const [batchPoNumber, setBatchPoNumber] = useState<string>('');
  const [isCreatePoModalOpen, setIsCreatePoModalOpen] = useState<boolean>(false);
  const [isSavingBatchPo, setIsSavingBatchPo] = useState<boolean>(false);
  const [isBatchEditPopoverOpen, setIsBatchEditPopoverOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [hasFetchedTableData, setHasFetchedTableData] = useState<boolean>(false);
  const [refetchTableData, setRefetchTableData] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<any>('');
  const [searchSubmitTrigger, setSearchSubmitTrigger] = useState(0);
  const debouncedSearch = useDebounce(searchTerm, SEARCH_INPUT_DEBOUNCE_MS, searchSubmitTrigger);
  // const [itemStatuses, setItemStatuses] = useState<any[]>(initial_statuses);
  const [filtersToMap, setFiltersToMap] = useState<any[]>(() =>
    createInitialFilters(includeClientFilter)
  );
  const [filters, setFilters] = useState<any>(initialFilterState);
  const [sort, setSort] = useState<any>({
    field: initialSortField,
    position: 1,
  });
  const [columnVisibility, setColumnVisibility] = useState<OrderingColumnVisibility>(
    DEFAULT_ORDERING_COLUMN_VISIBILITY
  );
  const [customColumnsConfig, setCustomColumnsConfig] = useState<CustomColumnPreference[]>([]);
  const normalizedCustomColumns = useMemo(
    () => normalizeCustomColumnsConfig(customColumnsConfig),
    [customColumnsConfig]
  );
  const visibleCustomColumns = useMemo(
    () =>
      canCustomizeCustomColumns
        ? normalizedCustomColumns
        : normalizedCustomColumns.filter((column) => isConfiguredCustomColumn(column)),
    [canCustomizeCustomColumns, normalizedCustomColumns]
  );
  const hiddenCustomColumnKeys = useMemo<OrderingColumnKey[]>(
    () =>
      canCustomizeCustomColumns
        ? []
        : normalizedCustomColumns
            .filter((column) => !isConfiguredCustomColumn(column))
            .map((column) => CUSTOM_COLUMN_VISIBILITY_KEY_BY_ID[column.id as CustomColumnId]),
    [canCustomizeCustomColumns, normalizedCustomColumns]
  );
  const hiddenColumnKeySet = useMemo(
    () => new Set<OrderingColumnKey>([...baseHiddenColumnKeys, ...hiddenCustomColumnKeys]),
    [baseHiddenColumnKeys, hiddenCustomColumnKeys]
  );
  const [supplierModal, setSupplierModal] = useState<any>({
    active: false,
    item: undefined,
  });
  const [isMyShopModalOpen, setIsMyShopModalOpen] = useState<boolean>(false);
  const [myShopItemId, setMyShopItemId] = useState<number | null>(null);
  const [myShopItemClassId, setMyShopItemClassId] = useState<number | null>(null);
  const [sheetCategories, setSheetCategories] = useState<any[]>([]);
  const [sheetSuppliers, setSheetSuppliers] = useState<any[]>([]);
  const [isLoadingOpen, setIsLoadingOpen] = useState<boolean>(false);
  const [workOrderPdfStatus, setWorkOrderPdfStatus] = useState<string>('none');
  // const [workOrderPdfContent, setWorkOrderPdfContent] = useState<any>(null);
  const [disabledDoc, setDisabledDoc] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState<boolean>(false);
  const [data, setData] = useState<any[]>([]);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [ordersLength, setOrdersLength] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageLimit, setPageLimit] = useState<number>(() => {
    return typeof window !== 'undefined' && window.localStorage.getItem('orderingPaginationLimit')
      ? Number(window.localStorage.getItem('orderingPaginationLimit'))
      : 25;
  });
  const missingComponentDeepLinkKeyRef = useRef<string | null>(null);

  useEffect(() => {
    setFiltersToMap(createInitialFilters(includeClientFilter));
  }, [includeClientFilter]);

  useEffect(() => {
    if (!accountId) {
      return;
    }

    const loadColumnVisibilityPreference = async () => {
      try {
        const [clientVisibilityPreference, sharedCustomColumnsPreference] = await Promise.all([
          getColumnVisibilityPreference({
            accountId,
            page: COLUMN_VISIBILITY_PAGES.clientOrderingAndReceiving,
          }),
          getColumnVisibilityPreference({
            accountId,
            page: COLUMN_VISIBILITY_PAGES.orderingAndReceiving,
          }),
        ]);
        setColumnVisibility(
          resolveColumnVisibility(
            normalizeColumnVisibilityPreference(
              ORDERING_COLUMN_KEYS,
              clientVisibilityPreference?.visibility?.columns
            )
          )
        );
        setCustomColumnsConfig(
          Array.isArray(sharedCustomColumnsPreference?.visibility?.customColumns)
            ? sharedCustomColumnsPreference.visibility.customColumns
            : []
        );
      } catch (error) {
        console.error(
          'Failed to load column visibility preferences for client ordering & receiving page:',
          error
        );
      }
    };

    void loadColumnVisibilityPreference();
  }, [accountId]);

  const handleSelectRows = (item: any, itemIndex?: number) => {
    const rowKey = getRowSelectionKey(item, itemIndex);
    setSelectedRows((prev) => {
      if (prev.includes(rowKey)) {
        return prev.filter((key) => key !== rowKey);
      }
      return [...prev, rowKey];
    });
  };

  const selectedRowSet = useMemo(() => new Set(selectedRows), [selectedRows]);

  const handleCloseSupplierModal = () => {
    setSupplierModal((prev: any) => {
      return { ...prev, active: false, item: undefined };
    });
  };

  const handleOpenSupplierModal = (val: boolean, item?: any) => {
    let payload = item;
    if (item) {
      payload = {
        ...item,
        additionalContacts: item?.additionalContacts || item?.additionalContactInfos || [],
      };
    }
    setSupplierModal((prev: any) => {
      let temp = { ...prev, active: val, item: payload || undefined };
      return temp;
    });
  };

  const handleSupplierChanged = async (supplier: SuppliersModule.Supplier): Promise<void> => {};

  const handleLoading = (val: boolean) => {
    setLoading(val);
  };

  const handleSort = (field: any) => {
    setSort((prev: any) => {
      if (prev.field !== field) {
        return { field, position: 1 };
      }
      return {
        field: prev?.position === 2 ? initialSortField : field,
        position: prev.position === 1 ? 2 : 1,
      };
    });
  };

  // const sortedData = useMemo(() => {
  //   const direction = sort?.position === 2 ? -1 : 1;
  //   const decorated = [...data].map((row, index) => ({ row, index }));

  //   const getSortValue = (row: any, field: string) => {
  //     const { category, subcategory, patternObj } = getCategoryData(row);
  //     const invoiceData = getInvoiceData(row);
  //     switch (field) {
  //       case 'supplier':
  //         return getSupplierItem(row)?.displayName || '';
  //       case 'category':
  //         return `${category?.name || category?.item || ''} ${subcategory?.name || ''}`.trim();
  //       case 'orderingDescription':
  //         return patternObj?.orderingDescription || row?.component?.estimateDescription || '';
  //       case 'qty':
  //         return getQtyNumericValue(row);
  //       case 'client':
  //         return getClientDisplayName(row);
  //       case 'clientParent':
  //         return getParentClientDisplayName(row) || '';
  //       case 'itemRoom':
  //         return `${getItemDisplayName(row)} ${getRoomDisplayName(row)}`.trim();
  //       case 'invoice':
  //         return [
  //           invoiceSortRank[normalizeText(invoiceData.status)] ?? -1,
  //           normalizeText(invoiceData.number),
  //           dateSortValue(invoiceData.date),
  //         ];
  //       case 'purchaser':
  //         return getPurchaserDisplayName(row);
  //       case 'itemStatus':
  //         return row?.order?.status || '';
  //       case 'installDate':
  //         return dateSortValue(getInstallationDateValue(row));
  //       case 'dueDate':
  //         return dateSortValue(getDueDateValue(row));
  //       case 'orderedDate':
  //         return dateSortValue(getOrderedDateValue(row));
  //       case 'receivedDate':
  //         return dateSortValue(getReceivedDateValue(row));
  //       case 'etaDate':
  //         return dateSortValue(getEtaDateValue(row));
  //       default:
  //         return dateSortValue(getDueDateValue(row));
  //     }
  //   };

  //   decorated.sort((left, right) => {
  //     const leftValue = getSortValue(left.row, sort?.field || initialSortField);
  //     const rightValue = getSortValue(right.row, sort?.field || initialSortField);

  //     const compareArrayValue = (leftArray: any[], rightArray: any[]) => {
  //       const maxLength = Math.max(leftArray.length, rightArray.length);
  //       for (let idx = 0; idx < maxLength; idx += 1) {
  //         const comparison = compareValues(leftArray[idx], rightArray[idx]);
  //         if (comparison !== 0) {
  //           return comparison;
  //         }
  //       }
  //       return 0;
  //     };

  //     const comparison =
  //       Array.isArray(leftValue) && Array.isArray(rightValue)
  //         ? compareArrayValue(leftValue, rightValue)
  //         : compareValues(leftValue, rightValue);

  //     if (comparison !== 0) {
  //       return comparison * direction;
  //     }

  //     return left.index - right.index;
  //   });

  //   return decorated.map((entry) => entry.row);
  // }, [data, sort]);

  const syncHorizontalScroll = (source: HTMLDivElement | null, target: HTMLDivElement | null) => {
    if (!source || !target) return;
    if (syncingHorizontalScroll.current) return;
    if (target.scrollLeft === source.scrollLeft) return;
    syncingHorizontalScroll.current = true;
    target.scrollLeft = source.scrollLeft;
    requestAnimationFrame(() => {
      syncingHorizontalScroll.current = false;
    });
  };

  const handleHeaderWrapperScroll: React.UIEventHandler<HTMLDivElement> = (event) => {
    syncHorizontalScroll(event.currentTarget, bodyWrapperRef.current);
  };

  const handleBodyWrapperScroll: React.UIEventHandler<HTMLDivElement> = (event) => {
    syncHorizontalScroll(event.currentTarget, headerWrapperRef.current);
  };

  const handleChangeValueSearchInput = (e: any) => {
    const { value } = e.target;
    setSearchTerm(value);
  };

  const handleSearchInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    setSearchSubmitTrigger((prev) => prev + 1);
  };

  const handleClearSearchKeyword = () => {
    setSearchTerm('');
  };

  const handleRefetchTableData = () => {
    setRefetchTableData((prev) => !prev);
  };

  const handleClearAllFilter = (filter_field: any) => {
    switch (filter_field) {
      case 'client':
      case 'supplier':
      case 'type':
      case 'purchasers':
      case 'orderingStatus':
      case 'itemStatus':
      case 'invoice':
        setFilters((prev: any) => {
          return {
            ...prev,
            [filter_field]: {
              ...prev[filter_field],
              value: [],
            },
          };
        });
        break;
      default:
        return;
    }

    setPageNumber(1);

    if (!isMobile) {
      handleRefetchTableData();
    }
  };

  const handleResetAllFilters = () => {
    setFilters(initialFilterState);
    setPageNumber(1);
    handleRefetchTableData();
  };

  const handleChooseFilter = (filter: string, filter_field: any) => {
    switch (filter_field) {
      case 'supplier':
      case 'type':
      case 'itemStatus':
      case 'invoice':
        setFilters((prev: any) => {
          const currentValues = prev?.[filter_field]?.value || [];
          const nextValues = currentValues.includes(filter)
            ? currentValues.filter((value: any) => value !== filter)
            : [...currentValues, filter];

          return {
            ...prev,
            [filter_field]: {
              ...prev[filter_field],
              value: nextValues,
            },
          };
        });
        break;
      case 'client':
      case 'purchasers':
      case 'orderingStatus':
        setFilters((prev: any) => {
          return {
            ...prev,
            [filter_field]: {
              ...prev[filter_field],
              value: filter ? [filter] : [],
            },
          };
        });
        break;
      default:
        return;
    }

    setPageNumber(1);

    if (!isMobile) {
      handleRefetchTableData();
    }
  };

  const handleColumnVisibilityChange = (next: OrderingColumnVisibility) => {
    setColumnVisibility(next);
    if (!accountId) {
      return;
    }
    void upsertColumnVisibilityPreference({
      accountId,
      page: COLUMN_VISIBILITY_PAGES.clientOrderingAndReceiving,
      visibility: { columns: next },
    }).catch((error) => {
      console.error(
        'Failed to save column visibility preferences for client ordering & receiving page:',
        error
      );
    });
  };

  const handleSaveCustomColumns = async (customColumns: CustomColumnPreference[]) => {
    if (!accountId) {
      return;
    }

    const response = await saveCustomColumnsPreference({
      accountId,
      page: COLUMN_VISIBILITY_PAGES.orderingAndReceiving,
      customColumns,
    });

    setCustomColumnsConfig(
      Array.isArray(response?.visibility?.customColumns)
        ? response.visibility.customColumns
        : customColumns
    );
  };

  const closeMyShopModal = () => {
    setIsMyShopModalOpen(false);
    setMyShopItemId(null);
    setMyShopItemClassId(null);
  };

  const handleMyShopModalClose = () => {
    closeMyShopModal();
    if (account?.isOwner || account?.isAdmin || account?.canSeePricing) {
      handleRefetchTableData();
    }
  };

  const loadSheetCategories = async (): Promise<any[]> => {
    if (sheetCategories.length > 0) {
      return sheetCategories;
    }

    const res = await getAllCategories({ isActiveOnly: true, accountId });
    const nextCategories = Array.isArray(res) ? res : [];
    setSheetCategories(nextCategories);
    return nextCategories;
  };

  const loadSheetSuppliers = async (): Promise<any[]> => {
    if (sheetSuppliers.length > 0) {
      return sheetSuppliers;
    }

    const res = await getSuppliers({ accountId });
    const nextSuppliers = Array.isArray(res) ? res : [];
    setSheetSuppliers(nextSuppliers);
    return nextSuppliers;
  };

  const handleOpenMyShopItemWideSheet = async (patternObj: any) => {
    const recordId = Number(patternObj?.id || 0);
    if (!recordId) {
      return;
    }

    setMyShopItemId(recordId);
    setMyShopItemClassId(
      Number.isFinite(Number(patternObj?.itemClassId)) ? Number(patternObj?.itemClassId) : null
    );
    setIsMyShopModalOpen(true);

    void Promise.all([loadSheetCategories(), loadSheetSuppliers()]);
  };

  const handleLoadSuppliers = async () => {
    let params = {
      accountId: accountId,
    };
    const res = await getSuppliers(params);
    if (res) {
      setFiltersToMap((prev) => {
        return prev?.map((f) => {
          if (f.value === 'supplier') {
            return {
              ...f,
              items: res?.map((s) => {
                return {
                  ...s,
                  label: s?.displayName,
                  value: s?.displayName,
                };
              }),
            };
          }
          return f;
        });
      });
    }
  };

  const handleGetItemStatuses = async () => {
    if (!accountId) {
      console.error('Account ID is missing.');
      return;
    }

    const payload = {
      accountId: accountId,
    };

    const res = await getAllStatuses(payload);
    if (res && !res.error) {
      const mapped_res =
        res && res.length > 0
          ? res.map((s: any) => {
              return {
                ...s,
                value: s.name,
              };
            })
          : [];
      let finalArray = [...mapped_res, ...initial_statuses]?.map((i: any, idx: number) => {
        return { id: idx + 1, label: i?.name, value: i?.value };
      });
      // setItemStatuses(finalArray);
      setFiltersToMap((prev) => {
        return prev?.map((item) => {
          if (item?.value === 'itemStatus') {
            return {
              ...item,
              items: finalArray,
            };
          }
          return item;
        });
      });
    }
  };

  const handleLoadClients = async (): Promise<void> => {
    if (!includeClientFilter) {
      return;
    }

    let res: any;
    if (accountId) {
      res = await getAllClients({
        accountId,
        sort: { column: 'displayName', value: 'ASC' },
        includeInactive: false,
      });
    }

    if (res && !res.error) {
      const convertToSortTableRowInfo = (client: any) => {
        const rowInfo: any = client;
        if (client.subClients) {
          rowInfo.subList = client.subClients.map(convertToSortTableRowInfo);
        }
        return rowInfo;
      };

      const clients = res.map(convertToSortTableRowInfo);
      const allSubClients = clients.reduce(
        (acc: any, client: any) => acc.concat(client.subList || []),
        [] as any[]
      );

      const updatedClients = [...clients, ...allSubClients]
        .filter((f) => f.active)
        .filter((f) => !f.parent);

      // function flattenClients(clients: any[], level = 1): any[] {
      //   return clients.flatMap((client, idx) => {
      //     const current = {
      //       id: idx + 1 + 1,
      //       label: client?.displayName,
      //       value: client?.displayName,
      //       parentLevel: level,
      //     };

      //     const children =
      //       client.subClients && client.subClients.length > 0
      //         ? flattenClients(client.subClients, level + 1)
      //         : [];

      //     return [current, ...children];
      //   });
      // }

      // const flattenedClients = flattenClients(updatedClients);

      setFiltersToMap((prev) => {
        return prev?.map((i) => {
          if (i?.value === 'client') {
            return {
              ...i,
              items: [
                ...updatedClients.map((i) => {
                  return {
                    ...i,
                    label: i?.displayName,
                    value: i?.displayName,
                  };
                }),
              ],
            };
          }
          return i;
        });
      });
    }
  };

  const handleFetchTableData = async () => {
    setHasFetchedTableData(false);
    handleLoading(true);
    try {
      const scopedFilters = {
        ...(includeClientFilter ? filters : { ...filters, client: { value: [] } }),
        ...(clientScopeId ? { clientId: clientScopeId } : {}),
      };

      const payload = {
        accountId,
        searchTerm: debouncedSearch,
        filters: scopedFilters,
        page: pageNumber,
        limit: pageLimit,
        sort,
      };

      const res = await getClientOrderingAndReceivingComponents(payload);

      if (!res || res?.error) {
        const message = res?.message || 'Error while fetching table data';
        console.error(message);
        toast.error('Failed to load Ordering & Receiving', {
          description: message,
        });
        return;
      }

      setFiltersToMap((prev) =>
        prev.map((filter) => {
          if (filter?.value !== 'type') {
            return filter;
          }
          return {
            ...filter,
            items: Array.isArray(res?.usedCategories) ? res.usedCategories : [],
          };
        })
      );

      setData(Array.isArray(res?.data?.items) ? res.data.items : []);
      setTotalPages(Math.max(1, Number(res?.data?.pagination?.totalPages || 1)));
      setOrdersLength(Number(res?.data?.pagination?.total || 0));
    } catch (error) {
      console.error('Error while fetching client ordering and receiving data', error);
      toast.error('Failed to load Ordering & Receiving', {
        description: 'Please try again.',
      });
    } finally {
      setHasFetchedTableData(true);
      handleLoading(false);
    }
  };

  useEffect(() => {
    setSelectedRows([]);
  }, [data]);

  const handleVisitClient = (id: number | null) => {
    if (!id) return;
    props.history.push(`/clients/${id}`);
  };

  const handleOpenOrder = (type: string, item?: any) => {
    const normalizedType = type === 'roman-shade' ? 'roman-shade-order' : type;

    if (normalizedType === 'purchased-goods') {
      const purchasedGoodsId = toOptionalPositiveInt(
        item?.purchasedGoodsId ?? item?.purchasedGoodId ?? item?.itemId ?? item?.id
      );
      if (typeof props.handleOpenSinglePurchasedGoods === 'function') {
        props.handleOpenSinglePurchasedGoods(purchasedGoodsId ?? null);
      }
      return;
    }

    const clientId = toOptionalPositiveInt(item?.clientId ?? item?.client?.id);
    const draperyOrderId = toOptionalPositiveInt(
      item?.draperyOrderId ?? item?.orderId ?? item?.itemId ?? item?.draperyOrder?.id
    );
    if (!draperyOrderId) {
      return;
    }
    const clientDisplayName = item?.clientDisplayName ?? item?.client?.displayName ?? undefined;
    const quantityRaw = Number(item?.quantity ?? item?.qty);
    const quantity = Number.isFinite(quantityRaw) ? quantityRaw : undefined;
    const tab = 'summary';

    let draperyItem: any = localStorage.getItem('draperyItem');
    draperyItem = draperyItem ? JSON.parse(draperyItem) : {};
    draperyItem.from = location.pathname;

    let romanShadeItem: any = localStorage.getItem('romanShadeItem');
    romanShadeItem = romanShadeItem ? JSON.parse(romanShadeItem) : {};
    romanShadeItem.from = location.pathname;

    localStorage.setItem('draperyItem', JSON.stringify(draperyItem));
    localStorage.setItem('romanShadeItem', JSON.stringify(romanShadeItem));

    const calculatorType = ['drapery-order', 'custom-order', 'roman-shade-order'].includes(
      normalizedType
    )
      ? normalizedType
      : 'drapery-order';

    props.history.push(`/calculator?type=${calculatorType}`, {
      clientId,
      draperyOrderId,
      clientDisplayName,
      tab,
      type: calculatorType,
      quantity,
      referrer: location.pathname,
    });
  };

  const openOrderFromItemRoom = (row: any, rowClientDisplayName?: string) => {
    const resolvedOrderId = toOptionalPositiveInt(
      row?.order?.id ?? row?.orderId ?? row?.id ?? row?.itemId ?? row?.draperyOrderId
    );
    const normalizedType =
      row?.order?.orderType === 'roman-shade' ? 'roman-shade-order' : row?.order?.orderType || '';

    if (normalizedType === 'purchased-goods') {
      if (typeof props.handleOpenSinglePurchasedGoods === 'function') {
        props.handleOpenSinglePurchasedGoods(resolvedOrderId ?? null);
      }
      return;
    }

    handleOpenOrder(row?.order?.orderType || '', {
      id: resolvedOrderId,
      itemId: resolvedOrderId,
      orderId: resolvedOrderId,
      draperyOrderId: resolvedOrderId,
      purchasedGoodsId: resolvedOrderId,
      purchasedGoodId: resolvedOrderId,
      clientId: row?.order?.client?.id,
      client: row?.order?.client,
      clientDisplayName: rowClientDisplayName ?? row?.order?.client?.displayName,
      quantity: row?.component?.quantity,
      qty: row?.component?.qty,
    });
  };

  const handleProjectWorkOrderNumClicked = async (id: number) => {
    if (!id) return;

    const draperyPdf = workOrderPdfStatus;
    if (draperyPdf === 'none') {
      const res = await getWorkOrderPDFContent(id);

      if (res.error) {
        return;
      }
      setIsLoadingOpen(true);
      setWorkOrderPdfStatus('downloading');
      // setWorkOrderPdfContent(res);

      const resDraperyOrder = await getAllInfo(id);
      const workOrder: WorkOrderModule.WorkOrder = resDraperyOrder.workOrder;
      const draperyOrderDesign = resDraperyOrder?.draperyOrderDesign;
      const storeId = `${accountId}_pdfs`;
      let pdfLink: any;
      let buffer: any;
      let dueDate = res.dueDate;
      let dueDateForLambda = dueDate ? moment(dueDate).local().format('MM/DD/YYYY') : '';

      const needNewPDF = !moment
        .utc(workOrder.dueDatePdf || undefined)
        .startOf('day')
        .isSame(moment.utc(res.dueDate || undefined).startOf('day'));
      const responseForLambda = { ...res, dueDate: dueDateForLambda, needNewPDF: needNewPDF };

      const openBuffer = (pdfBuffer: Uint8Array) => {
        const normalizedBuffer = new Uint8Array(pdfBuffer).slice().buffer;
        const blob = new Blob([normalizedBuffer], {
          type: 'application/pdf',
        });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
      };

      const regeneratePdf = async () => {
        let nextDrawingUrl: string | null = null;
        if (draperyOrderDesign?.id) {
          try {
            nextDrawingUrl = await regenerateWorkOrderDrawing({
              accountId,
              design: draperyOrderDesign,
              orderType: resDraperyOrder?.orderType,
            });
          } catch (error) {
            console.error('Failed to regenerate work order drawing before PDF generation', error);
          }
        }

        const lambdaResponse = await callLambda(
          {
            ...responseForLambda,
            ...(nextDrawingUrl ? { draperyImage: nextDrawingUrl } : {}),
            needNewPDF: true,
          },
          accountId,
          '',
          workOrder
        );

        const { pdfData } = lambdaResponse;
        pdfLink = getPdfFileNameFromLambdaResponse(lambdaResponse);
        buffer = pdfData ? Buffer.from(pdfData, 'base64') : undefined;
        await editWorkOrder({
          ...workOrder,
          pdfLink,
          dueDatePdf: dueDateForLambda,
          pdfGeneratedAt: new Date(),
        });
      };

      if (!workOrder.pdfLink || needNewPDF) {
        await regeneratePdf();
      }

      if (buffer && pdfLink) {
        await storeFile(storeId, pdfLink, buffer, Config.fileStoreUrl);
      }

      if (buffer) {
        openBuffer(buffer);
      } else {
        const fileName = pdfLink ? pdfLink : workOrder.pdfLink;
        if (fileName) {
          try {
            await openStoredPdf(storeId, fileName, Config.fileStoreUrl);
          } catch (error) {
            if (!isStoredFileMissingError(error)) {
              throw error;
            }

            await regeneratePdf();

            if (buffer && pdfLink) {
              await storeFile(storeId, pdfLink, buffer, Config.fileStoreUrl);
              openBuffer(buffer);
            } else if (pdfLink) {
              await openStoredPdf(storeId, pdfLink, Config.fileStoreUrl);
            }
          }
        }
      }
      handleDraperyPdfFinished(true);
    }
  };

  const handleDraperyPdfFinished = (success: boolean) => {
    if (success) {
      setIsLoadingOpen(false);
      setWorkOrderPdfStatus('none');
    } else {
      setIsLoadingOpen(false);
      setWorkOrderPdfStatus('error');
    }
  };

  const toggleLoadingModal = () => {
    setIsLoadingOpen((prev) => !prev);
  };

  const getRowCustomColumnValue = (row: any, columnId: CustomColumnId): string => {
    const summaryComponent = getRowSummaryComponent(row);
    if (row?.source === 'purchased-goods-component') {
      return readCustomColumnValue(
        row?.component?.customColumnsValues ?? summaryComponent?.customColumnsValues,
        columnId
      );
    }

    return readCustomColumnValue(
      summaryComponent?.customColumnsValues ?? row?.component?.customColumnsValues,
      columnId
    );
  };

  const updateLocalCustomColumnValue = (row: any, columnId: CustomColumnId, value: string) => {
    const rowKey = getRowSelectionKey(row);
    setData((prev) =>
      prev.map((entry) => {
        if (getRowSelectionKey(entry) !== rowKey) {
          return entry;
        }

        if (entry?.source === 'purchased-goods-component') {
          const targetComponentId = String(row?.component?.id || '');
          const nextCustomColumnsValues = writeCustomColumnValue(
            entry?.component?.customColumnsValues ??
              entry?.component?.draperyOrderSummaryComponent?.customColumnsValues,
            columnId,
            value
          );
          const purchasedGoodsComponents = Array.isArray(entry?.order?.purchasedGoodsComponents)
            ? entry.order.purchasedGoodsComponents.map((component: any, index: number) => {
                const componentId = String(component?.id || '');
                const shouldUpdate =
                  targetComponentId.length > 0
                    ? componentId === targetComponentId ||
                      (componentId.length === 0 && index === row?.componentIndex)
                    : index === row?.componentIndex;
                if (!shouldUpdate) return component;
                return {
                  ...component,
                  customColumnsValues: writeCustomColumnValue(
                    component?.customColumnsValues,
                    columnId,
                    value
                  ),
                };
              })
            : entry?.order?.purchasedGoodsComponents;

          return {
            ...entry,
            component: {
              ...entry.component,
              customColumnsValues: nextCustomColumnsValues,
              draperyOrderSummaryComponent: {
                ...(entry?.component?.draperyOrderSummaryComponent || {}),
                customColumnsValues: nextCustomColumnsValues,
              },
            },
            order: {
              ...entry.order,
              purchasedGoodsComponents,
            },
          };
        }

        if (entry?.source === 'drapery-summary-component') {
          const nextCustomColumnsValues = writeCustomColumnValue(
            entry?.component?.customColumnsValues,
            columnId,
            value
          );
          return {
            ...entry,
            component: {
              ...entry.component,
              customColumnsValues: nextCustomColumnsValues,
              draperyOrderSummaryComponent: {
                ...(entry?.component?.draperyOrderSummaryComponent || {}),
                customColumnsValues: nextCustomColumnsValues,
              },
            },
          };
        }

        return {
          ...entry,
          component: {
            ...entry.component,
            draperyOrderSummaryComponent: {
              ...(entry?.component?.draperyOrderSummaryComponent || {}),
              customColumnsValues: writeCustomColumnValue(
                entry?.component?.draperyOrderSummaryComponent?.customColumnsValues,
                columnId,
                value
              ),
            },
          },
        };
      })
    );
  };

  const updateLocalRowField = (row: any, field: string, value: any, dataIdx?: any) => {
    // const rowKey = getRowSelectionKey(row);
    setData((prev) =>
      prev.map((entry, idx) => {
        // if (getRowSelectionKey(entry) !== rowKey) {
        //   return entry;
        // }
        if (idx === dataIdx) {
          if (entry?.component?.draperyOrderSummaryComponent) {
            return {
              ...entry,
              component: {
                ...entry?.component,
                [field]: value || null,
                draperyOrderSummaryComponent: {
                  ...entry?.component?.draperyOrderSummaryComponent,
                  [field]: value || null,
                },
              },
            };
          } else {
            return {
              ...entry,
              component: {
                ...entry.component,
                [field]: value || null,
              },
            };
          }
        }
        return entry;

        // if (entry?.source === 'purchased-goods-component') {
        //   const targetComponentId = String(row?.component?.id || '');
        //   const purchasedGoodsComponents = Array.isArray(entry?.order?.purchasedGoodsComponents)
        //     ? entry.order.purchasedGoodsComponents.map((component: any, index: number) => {
        //         const componentId = String(component?.id || '');
        //         const shouldUpdate =
        //           targetComponentId.length > 0
        //             ? componentId === targetComponentId ||
        //               (componentId.length === 0 && index === row?.componentIndex)
        //             : index === row?.componentIndex;
        //         return shouldUpdate ? { ...component, [field]: value } : component;
        //       })
        //     : entry?.order?.purchasedGoodsComponents;

        //   return {
        //     ...entry,
        //     component: {
        //       ...entry.component,
        //       [field]: value,
        //     },
        //     order: {
        //       ...entry.order,
        //       purchasedGoodsComponents,
        //     },
        //   };
        // }

        // if (entry?.source === 'drapery-summary-component') {
        //   return {
        //     ...entry,
        //     component: {
        //       ...entry.component,
        //       [field]: value,
        //     },
        //   };
        // }

        // return {
        //   ...entry,
        //   component: {
        //     ...entry.component,
        //     draperyOrderSummaryComponent: {
        //       ...(entry?.component?.draperyOrderSummaryComponent || {}),
        //       [field]: value,
        //     },
        //   },
        // };
      })
    );
  };

  const persistRowField = async (
    row: any,
    field: 'po' | 'orderedDate' | 'receivedDate' | 'eta',
    value: any,
    options?: { silent?: boolean },
    dataIdx?: number
  ) => {
    updateLocalRowField(row, field, value, dataIdx);

    const getFieldMessages = (
      targetField: 'po' | 'orderedDate' | 'receivedDate' | 'eta'
    ): { success: string; error: string } => {
      if (targetField === 'po') {
        return {
          success: 'Successfully updated PO number.',
          error: 'Failed to update PO number',
        };
      }

      const colField =
        targetField === 'orderedDate'
          ? 'Ordered'
          : targetField === 'receivedDate'
            ? 'Received'
            : 'ETA';

      return {
        success: `Successfully updated ${colField} field.`,
        error: `Failed to update ${colField} field`,
      };
    };

    const fieldMessages = getFieldMessages(field);

    try {
      const payload = {
        id: row?.order?.id,
        field: field === 'eta' ? 'etaDate' : field,
        value,
        componentSource: row?.source,
        componentId: row?.component?.id ?? null,
        summaryComponentId: getRowSummaryComponent(row)?.id ?? null,
        componentIndex: row?.componentIndex ?? null,
        purchasedGoodsComponentsType: row?.order?.purchasedGoodsComponentsType,
        orderId: row?.order?.id,
      };
      const response = await updateClientOrderingAndReceivingTable(payload);
      if (!response || response?.error) {
        throw new Error(fieldMessages.error);
      }

      if (!options?.silent) {
        toast(fieldMessages.success);
      }
      return true;
    } catch (error: any) {
      console.error(error);
      if (!options?.silent) {
        toast.error(fieldMessages.error);
      }
      handleRefetchTableData();
      return false;
    }
  };

  const persistCustomColumnValue = async (row: any, columnId: CustomColumnId, value: string) => {
    updateLocalCustomColumnValue(row, columnId, value);

    try {
      const payload = {
        id: row?.order?.id,
        field: 'customColumn',
        customColumnId: columnId,
        value,
        componentSource: row?.source,
        componentId: row?.component?.id ?? null,
        summaryComponentId: getRowSummaryComponent(row)?.id ?? null,
        componentIndex: row?.componentIndex ?? null,
        purchasedGoodsComponentsType: row?.order?.purchasedGoodsComponentsType,
      };
      const response = await updateClientOrderingAndReceivingTable(payload);
      if (!response || response?.error) {
        throw new Error('Failed to update custom column.');
      }
      return true;
    } catch (error) {
      console.error(error);
      toast.error('Failed to update custom column');
      handleRefetchTableData();
      return false;
    }
  };

  const handleSelectAllRows = (checked: boolean) => {
    if (!checked) {
      setSelectedRows([]);
      return;
    }
    // setSelectedRows(sortedData.map((row, index) => getRowSelectionKey(row, index)));
    setSelectedRows(data.map((row, index) => getRowSelectionKey(row, index)));
  };

  const selectedRowItems = useMemo(
    // () => sortedData.filter((row, index) => selectedRowSet.has(getRowSelectionKey(row, index))),
    () => data.filter((row, index) => selectedRowSet.has(getRowSelectionKey(row, index))),
    // [selectedRowSet, sortedData]
    [selectedRowSet, data]
  );

  const handleBatchDateUpdate = async (field: 'orderedDate' | 'receivedDate') => {
    if (selectedRowItems.length <= 0) return;
    const today = new Date().toISOString();

    for (const row of selectedRowItems) {
      if (field === 'orderedDate' && getPurchaserDisplayName(row) === 'COM') {
        continue;
      }
      await persistRowField(row, field, today, { silent: true });
    }

    setSelectedRows([]);
  };

  const handleOpenCreatePoModal = () => {
    setIsBatchEditPopoverOpen(false);
    setBatchPoNumber('');
    setIsCreatePoModalOpen(true);
  };

  const handleCloseCreatePoModal = () => {
    if (isSavingBatchPo) {
      return;
    }
    setIsCreatePoModalOpen(false);
    setBatchPoNumber('');
  };

  const handleSaveBatchPoNumber = async () => {
    const poNumber = batchPoNumber.trim();
    if (!poNumber) {
      toast.error('PO number is required.');
      return;
    }

    const selectedItems = selectedRowItems.filter((row) => row?.order?.id);
    if (selectedItems.length <= 0) {
      toast.error('Please select at least one row.');
      return;
    }

    setIsSavingBatchPo(true);

    let updatedCount = 0;

    for (const row of selectedItems) {
      const payload = {
        id: row?.order?.id,
        field: 'po',
        value: poNumber,
        componentSource: row?.source,
        componentId: row?.component?.id ?? null,
        summaryComponentId: getRowSummaryComponent(row)?.id ?? null,
        componentIndex: row?.componentIndex ?? null,
      };

      try {
        const response = await updateClientOrderingAndReceivingTable(payload);
        if (response && !response.error) {
          updatedCount += 1;
          updateLocalRowField(row, 'po', poNumber);
        }
      } catch (error) {
        console.error('Failed to update batch PO number:', error);
      }
    }

    const failedCount = selectedItems.length - updatedCount;

    setIsSavingBatchPo(false);
    setIsCreatePoModalOpen(false);
    setBatchPoNumber('');

    if (updatedCount > 0) {
      setSelectedRows([]);
      toast('Batch update complete', {
        description:
          failedCount > 0
            ? `PO number saved for ${updatedCount} row${updatedCount === 1 ? '' : 's'}. ${failedCount} failed.`
            : `PO number saved for ${updatedCount} row${updatedCount === 1 ? '' : 's'}.`,
      });
      if (failedCount > 0) {
        handleRefetchTableData();
      }
      return;
    }

    toast.error('Failed to update PO number');
    handleRefetchTableData();
  };

  const handleDeleteUpload = async (item: any, items: any[], row: any) => {
    if (!item) return;
    if (!items || items?.length <= 0) return;
    if (!row?.order?.draperyOrderUploadId) return;
    setDisabledDoc(true);

    let filtered = items?.filter((f) => f.source !== item?.source);
    const payload = {
      id: row?.order?.draperyOrderUploadId,
      items: filtered,
    };
    try {
      await deleteDraperyOrderUpload(payload);
      toast('File deleted', {
        description: 'The file has been removed successfully.',
      });
      setData((prev) => {
        return prev?.map((i) => {
          if (i?.order?.id === row?.order?.id) {
            return {
              ...i,
              uploads: filtered,
            };
          }
          return i;
        });
      });
      setDisabledDoc(false);
      // handleRefetchTableData();
    } catch (error) {
      console.error('Error while deleting document');
      const message = 'Failed to delete file. Please try again';
      toast.error('Delete failed', {
        description: message,
      });
      setDisabledDoc(false);
    }
  };

  const handleDownloadUploadDoc = (url: string) => {
    if (!url) return;
    let downloadName = url ? url.substring(url.lastIndexOf('/') + 1) : '';
    let downloadUrl = Config.fileStoreUrl + url;
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = downloadName || 'image.jpg';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const inferUploadTypeFromSource = (source: string): string => {
    const extension = (source || '').split('?')[0].split('.').pop()?.toLowerCase();
    if (!extension) return 'application/octet-stream';
    if (extension === 'jpg' || extension === 'jpeg') return 'jpg';
    if (extension === 'png') return 'png';
    if (extension === 'svg') return 'svg';
    if (extension === 'webp') return 'webp';
    if (extension === 'pdf') return 'pdf';
    return extension;
  };

  const buildRemoteSourcePath = (remoteKey: string): string => {
    if (!remoteKey) {
      return '';
    }
    return remoteKey.startsWith('/') ? remoteKey : `/${remoteKey}`;
  };

  const sanitizeFileName = (fileName: string): string => {
    if (!fileName) {
      return 'upload.pdf';
    }
    return fileName.replace(/[^a-z0-9._() -]/gi, '_');
  };

  const buildRemoteFileName = (fileName: string): string => {
    const sanitized = sanitizeFileName(fileName || 'upload.pdf');
    const parts = sanitized.split('.');
    const extension = parts.length > 1 ? parts.pop() || 'pdf' : 'pdf';
    const base = parts.join('.') || 'upload';
    return `${base}.${extension}`;
  };

  const inferUploadTypeFromFile = (file: File): string => {
    const fileName = (file.name || '').toLowerCase();
    const extension = fileName.split('.').pop() || '';
    if (extension) {
      if (extension === 'jpeg') return 'jpg';
      return extension;
    }
    const mime = (file.type || '').toLowerCase();
    if (mime.includes('pdf')) return 'pdf';
    if (mime.includes('jpeg') || mime.includes('jpg')) return 'jpg';
    if (mime.includes('png')) return 'png';
    if (mime.includes('svg')) return 'svg';
    if (mime.includes('webp')) return 'webp';
    return 'application/octet-stream';
  };

  const uploadFileWithPresign = async (file: File, uploadType: 'pdf' | 'image') => {
    if (!accountId) throw new Error('Missing accountId');
    const storeId = `${accountId}_${uploadType === 'pdf' ? 'pdfs' : 'images'}`;
    const inferredType = inferUploadTypeFromFile(file);
    const fallbackName = uploadType === 'pdf' ? 'upload.pdf' : `upload.${inferredType || 'png'}`;
    const generatedFileName = buildRemoteFileName(file.name || fallbackName);
    const contentType =
      file.type || (uploadType === 'pdf' ? PDF_CONTENT_TYPE : 'application/octet-stream');

    const presignResponse = await generatePresignedUrl({
      storeId,
      fileName: generatedFileName,
      method: 'PUT',
      contentType,
      preserveFileName: true,
    });
    const resolvedFileName = presignResponse.fileName || generatedFileName;

    const response = await fetch(presignResponse.url, {
      method: presignResponse.method || 'PUT',
      headers: {
        'Content-Type': contentType,
        'Content-Disposition': `attachment; filename="${resolvedFileName}"`,
      },
      body: file,
    });

    if (!response.ok) {
      throw new Error(`Failed to upload file via presigned URL. Status: ${response.status}`);
    }

    const remoteKey = presignResponse.key;
    return {
      remoteKey,
      remoteFileName: resolvedFileName,
      remoteSource: buildRemoteSourcePath(remoteKey),
    };
  };

  const convertToPng = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject('Canvas context is not supported.');
          return;
        }
        canvas.width = image.width;
        canvas.height = image.height;
        ctx.drawImage(image, 0, 0);
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject('Conversion to PNG failed.');
              return;
            }
            const pngFile = new File([blob], file.name.replace(/\.\w+$/, '.png'));
            const pngUrl = URL.createObjectURL(pngFile);
            resolve(pngUrl);
          },
          'image/png',
          1
        );
      };
      image.onerror = () => {
        reject('Image loading failed.');
      };
      image.src = URL.createObjectURL(file);
    });
  };

  const handleFile = async (files: File[] | FileList, item: any) => {
    if (!item) return;
    if (!files || files.length === 0) {
      return;
    }
    setUploadError(null);
    setDisabledDoc(true);

    const filesToProcess = Array.from(files);
    const preparedUploads: any[] = [];

    for (let i = 0; i < filesToProcess.length; i++) {
      const file = filesToProcess[i];
      const fileName = (file.name || '').toLowerCase();
      const extension = fileName.split('.').pop() || '';
      const mimeType = (file.type || '').toLowerCase();
      const isPdf = extension === 'pdf' || mimeType === 'application/pdf';
      const isImageExtension = ALLOWED_IMAGE_EXTENSIONS.some((ext) => fileName.endsWith(ext));

      if (!isPdf && !isImageExtension) {
        setUploadError('Valid file types: JPEG, PNG, SVG, WEBP, PDF.');
        continue;
      }

      if (file.size / 1024 > MAX_FILE_SIZE_KB) {
        setUploadError('Max allowed size is 10MB.');
        continue;
      }

      const normalizedFileType = inferUploadTypeFromFile(file);

      if (isPdf) {
        const upload: any = file;
        upload.source = URL.createObjectURL(file);
        upload.description = '';
        upload.fromRest = false;
        upload.isPdf = true;
        upload.blobToSend = file;
        upload.fileType = normalizedFileType;
        upload.mimeType = file.type || PDF_CONTENT_TYPE;

        if (!Config.fileStoreUrl.includes('localhost')) {
          try {
            const remoteInfo = await uploadFileWithPresign(file, 'pdf');
            upload.remoteKey = remoteInfo.remoteKey;
            upload.remoteFileName = remoteInfo.remoteFileName;
            upload.remoteSource = remoteInfo.remoteSource;
            upload.remoteUploaded = true;
          } catch (error) {
            console.error('Presigned PDF upload failed:', error);
            setUploadError(PDF_UPLOAD_ERROR_MESSAGE);
            continue;
          }
        }

        preparedUploads.push(upload);
        continue;
      }

      let obj: any = {};

      if (!Config.fileStoreUrl.includes('localhost')) {
        try {
          const remoteInfo = await uploadFileWithPresign(file, 'image');
          obj = file;
          obj.source = URL.createObjectURL(file);
          obj.description = '';
          obj.fromRest = false;
          obj.fileType = normalizedFileType;
          obj.mimeType = file.type || 'application/octet-stream';
          obj.blobToSend = file;
          obj.remoteKey = remoteInfo.remoteKey;
          obj.remoteFileName = remoteInfo.remoteFileName;
          obj.remoteSource = remoteInfo.remoteSource;
          obj.remoteUploaded = true;
        } catch (error) {
          console.error('Presigned image upload failed:', error);
          setUploadError('Failed to upload image. Please try again.');
          continue;
        }
      } else if (fileName.endsWith('.svg') || fileName.endsWith('.webp')) {
        try {
          const pngUrl = await convertToPng(file);
          obj = file;
          obj.source = pngUrl;
          obj.description = '';
          obj.fromRest = false;
          obj.fileType = normalizedFileType;
          obj.mimeType = file.type;
          obj.blobToSend = file;
        } catch (error) {
          console.error(error);
        }
      } else {
        obj = file;
        obj.source = URL.createObjectURL(file);
        obj.description = '';
        obj.fromRest = false;
        obj.fileType = normalizedFileType;
        obj.mimeType = file.type;
        obj.blobToSend = file;
      }

      if (Object.keys(obj).length > 0) {
        preparedUploads.push(obj);
      }
    }

    if (!accountId || preparedUploads.length === 0) {
      return;
    }

    let uploadRecordId = item?.order?.draperyOrderUploadId;
    let existingItems = Array.isArray(item?.uploads) ? [...item?.uploads] : [];
    let uploadedCount = 0;
    let failedCount = 0;
    let lastErrorMessage = '';

    for (const upload of preparedUploads) {
      const formData = new FormData();
      const remoteUploadsPayload: Array<{
        source: string;
        type: string;
        remoteKey?: string;
        description?: string;
      }> = existingItems
        .filter((item: any) => typeof item?.source === 'string' && item.source)
        .map((item: any) => ({
          source: item.source,
          type: (item.type || inferUploadTypeFromSource(item.source) || 'application/octet-stream')
            .toString()
            .toLowerCase(),
          description: item.description || '',
        }));

      const hasRemoteUpload =
        (upload.remoteUploaded && (upload.remoteSource || upload.remoteKey)) ||
        (!!upload.remoteSource && upload.isPdf);

      if (hasRemoteUpload) {
        const remoteSource =
          (typeof upload.remoteSource === 'string' && upload.remoteSource.length > 0
            ? upload.remoteSource
            : typeof upload.remoteKey === 'string'
              ? `/${upload.remoteKey}`
              : '') || '';

        if (remoteSource) {
          remoteUploadsPayload.push({
            source: remoteSource,
            type: upload.fileType || upload.mimeType || upload.type || 'application/pdf',
            remoteKey: upload.remoteKey,
            description: '',
          });
        }
      } else {
        if (!upload.blobToSend) {
          console.error('No blob to send');
          failedCount += 1;
          continue;
        }

        const croppedFile = new File([upload.blobToSend], upload.name, {
          type: upload.type,
          lastModified: Date.now(),
        });
        formData.append('files', croppedFile);
        formData.append('descriptions', '');
      }

      if (remoteUploadsPayload.length > 0) {
        formData.append('remoteUploads', JSON.stringify(remoteUploadsPayload));
      }

      formData.append('accountId', accountId.toString());
      formData.append('fileStoreUrl', Config.fileStoreUrl);
      formData.append('preserveFileName', 'true');

      try {
        let uploadResponse: any;
        if (uploadRecordId) {
          formData.append('id', uploadRecordId.toString());
          uploadResponse = await editDraperyOrderUpload(formData);
        } else {
          uploadResponse = await addDraperyOrderUpload(formData);
          if (uploadResponse?.id) {
            uploadRecordId = uploadResponse.id;
          }
        }

        const updatedItems = Array.isArray(uploadResponse?.items) ? uploadResponse.items : [];
        if (updatedItems.length > 0) {
          existingItems = updatedItems;
        }
        uploadedCount += 1;
      } catch (error) {
        failedCount += 1;
        lastErrorMessage =
          item?.order?.orderType === 'purchased-goods'
            ? 'Failed to upload one or more PO documents.'
            : 'Failed to upload one or more files.';
        console.error('Failed to upload file:', error);
      }
    }

    if (uploadedCount > 0) {
      const uploadLabel = item?.order?.orderType === 'purchased-goods' ? 'document' : 'file';
      const description =
        uploadedCount === 1
          ? uploadSuccessToast(item?.order?.orderType)?.description
          : `${uploadedCount} ${uploadLabel}${uploadedCount > 1 ? 's' : ''} uploaded successfully.`;
      toast(uploadSuccessToast(item?.order?.orderType)?.title, {
        description,
      });
      setData((prev) => {
        return prev?.map((i) => {
          if (i?.order?.id === item?.order?.id) {
            return {
              ...i,
              uploads: existingItems,
            };
          }
          return i;
        });
      });
      setDisabledDoc(false);
    }

    if (failedCount === 0 && uploadedCount > 0) {
    } else if (failedCount > 0) {
      const errorMessage =
        lastErrorMessage ||
        (item?.order?.orderType === 'purchased-goods'
          ? 'Failed to upload PO document. Please try again.'
          : 'Failed to upload file. Please try again.');
      setUploadError(errorMessage);
      setDisabledDoc(false);
      toast.error('Upload failed', {
        description:
          uploadedCount > 0 ? `${uploadedCount} uploaded, ${failedCount} failed.` : errorMessage,
      });
    }
  };

  const handleChangeFile = (e: React.ChangeEvent<HTMLInputElement>, item: any) => {
    if (!item) return;
    const files = e.target.files || [];
    handleFile(files, item);
  };

  const handleDrop = (e: React.DragEvent<HTMLLabelElement>, item: any) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFile(e.dataTransfer.files, item);
    }
  };

  const handlePreviewInNewTab = async (item: any) => {
    if (!item) return;
    if (!item?.avatarId) return;
    try {
      const fileUrl = resolveImageSrc(item?.avatarId);
      if (isAbsoluteImageSrc(item?.avatarId)) {
        window.open(fileUrl, '_blank');
        return;
      }
      const res = await fetch(fileUrl);
      if (!res.ok) {
        throw new Error('Failed to fetch image');
      }
      const originalBlob = await res.blob();
      const contentType = 'image/png';
      const blob = new Blob([originalBlob], { type: contentType });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
      }, 1000);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteItemImage = async (row: any) => {
    const fabricId = row?.component?.patternObj?.id;
    if (!fabricId) return;

    const payload = {
      id: row?.order?.id,
      field: 'avatarId',
      value: '',
      fabricId,
      componentSource: row?.source,
      componentId: row?.component?.id ?? null,
      summaryComponentId: getRowSummaryComponent(row)?.id ?? null,
      componentIndex: row?.componentIndex ?? null,
    };

    const imageRes = await updateClientOrderingAndReceivingTable(payload);
    if (!imageRes || imageRes?.error) {
      toast.error('Failed to remove photo');
      return;
    }

    setData((prev) => {
      return prev?.map((i: any) => {
        if (i?.component?.patternObj?.id === fabricId) {
          return {
            ...i,
            component: {
              ...i?.component,
              patternObj: {
                ...i?.component?.patternObj,
                avatarId: '',
              },
            },
          };
        }
        return i;
      });
    });
  };

  const handleImageUpload = async (file: File, row: any) => {
    setIsUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('accountId', String(accountId));
      formData.append('fileStoreUrl', Config.fileStoreUrl);

      const response = await uploadImage(formData);
      if (!response?.url) {
        toast.error('Failed to upload photo');
        return;
      }

      const fabricId = row?.component?.patternObj?.id;
      if (!fabricId) {
        toast.error('Failed to update photo');
        return;
      }

      const payload = {
        id: row?.order?.id,
        field: 'avatarId',
        value: response.url,
        fabricId,
        componentSource: row?.source,
        componentId: row?.component?.id ?? null,
        summaryComponentId: getRowSummaryComponent(row)?.id ?? null,
        componentIndex: row?.componentIndex ?? null,
      };
      const imageRes = await updateClientOrderingAndReceivingTable(payload);
      if (!imageRes || imageRes?.error) {
        toast.error('Failed to update photo');
        return;
      }

      setData((prev) => {
        return prev?.map((i: any) => {
          if (i?.component?.patternObj?.id === fabricId) {
            return {
              ...i,
              component: {
                ...i?.component,
                patternObj: {
                  ...i?.component?.patternObj,
                  avatarId: response.url || '',
                },
              },
            };
          }
          return i;
        });
      });
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleImageFileChange = async (event: ChangeEvent<HTMLInputElement>, row: any) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    await handleImageUpload(file, row);
    event.target.value = '';
  };

  const handleChooseDate = (date: Date | undefined, column: string, row: any, dataIdx?: any) => {
    if (!row) return;
    const nextValue = date ? new Date(date).toISOString() : null;
    const targetField = column === 'eta' ? 'eta' : column;
    if (!['orderedDate', 'receivedDate', 'eta'].includes(targetField)) {
      return;
    }
    void persistRowField(
      row,
      targetField as 'orderedDate' | 'receivedDate' | 'eta',
      nextValue,
      undefined,
      dataIdx
    );
  };

  const handleChangePoNumber = (e: ChangeEvent<HTMLInputElement>) => {
    let { value } = e.target;
    setPoNumberState(value);
  };

  const handleOnBlurPoNumber = async (item: any, rowPos: number) => {
    if (!item) return;
    if (isNaN(rowPos)) return;
    await persistRowField(item, 'po', poNumberState, undefined, rowPos);
    setRowIndex(null);
    setPoNumberState('');
  };

  // const handleChangeItemStatus = async (value: string, item: any) => {
  //   if (!value) return;
  //   if (!item) return;

  //   setData((prev) => {
  //     return prev?.map((i) => {
  //       if (i?.order?.id === item?.order?.id) {
  //         return {
  //           ...i,
  //           order: {
  //             ...i?.order,
  //             status: value,
  //           },
  //         };
  //       }
  //       return i;
  //     });
  //   });

  //   let payload = {
  //     id: item?.order?.id,
  //     status: value,
  //     field: 'itemStatus',
  //   };
  //   const res: any = await updateOrderingAndReceivingTable(payload);
  //   if (!res || res?.error) {
  //     toast.error('Failed to update item status');
  //     return;
  //   }
  //   toast('Successfully updated item status.');
  // };

  const handleCellColor = (value?: string) => {
    let className = 'base_status_color';
    if (value) {
      const defaultStatuses =
        initial_statuses && initial_statuses.length > 0
          ? initial_statuses.map((s: any) => s.value)
          : [];
      if (!defaultStatuses.includes(value)) {
        className = 'custom_status_color';
      } else {
        className = `status_${value.split(' ').join('_').toLowerCase()}`;
      }
    }
    return className;
  };

  const handlePageLimitChange = (e: ChangeEvent<HTMLSelectElement>): void => {
    const { value } = e.currentTarget;
    const numberValue = Number(value);
    if (isNaN(numberValue)) return;

    if (typeof window !== 'undefined') {
      window.localStorage.setItem('orderingPaginationLimit', value);
    }

    setPageNumber(1);
    setPageLimit(numberValue);
  };

  const handleNextPage = (): void => {
    setPageNumber((prev) => prev + 1);
  };

  const handlePrevPage = (): void => {
    if (pageNumber <= 1) return;
    setPageNumber((prev) => prev - 1);
  };

  const handleFirstPage = (): void => {
    setPageNumber(1);
  };

  const handleLastPage = (): void => {
    setPageNumber(totalPages);
  };

  const handleOpenLinkedPO = (number: string = '') => {
    if (!number) return;
    if (!props.canOpenPO) return;
    props.handleLinkedPO(number, 'pos');
  };

  const showColumn = (key: OrderingColumnKey) =>
    !hiddenColumnKeySet.has(key) && isColumnVisible(columnVisibility, key);

  useEffect(() => {
    handleFetchTableData();
    return () => {};
  }, [
    clientScopeId,
    refetchTableData,
    debouncedSearch,
    filters,
    pageNumber,
    pageLimit,
    sort.field,
    sort.position,
  ]);

  // useEffect(() => {
  //   if (sortedData && sortedData?.length <= 0) {
  //     setPageNumber(1);
  //   }
  // }, [sortedData]);
  useEffect(() => {
    if (data && data?.length <= 0) {
      setPageNumber(1);
    }
  }, [data]);

  useEffect(() => {
    if (loading) return;
    if (!hasFetchedTableData) return;
    if (typeof window === 'undefined') return;

    const params = new URLSearchParams(window.location.search);
    if (params.get('openNotebook') !== 'true') {
      missingComponentDeepLinkKeyRef.current = null;
      return;
    }
    if (String(params.get('tabType') || '').trim() !== 'components') {
      missingComponentDeepLinkKeyRef.current = null;
      return;
    }

    const componentId = Number(params.get('componentId') || 0);
    const hasComponentId = Number.isInteger(componentId) && componentId > 0;
    const componentKey = String(params.get('componentKey') || '').trim() || null;
    if (!hasComponentId && !componentKey) {
      missingComponentDeepLinkKeyRef.current = null;
      return;
    }

    const itemId = Number(params.get('itemId') || 0);
    if (!Number.isInteger(itemId) || itemId <= 0) {
      missingComponentDeepLinkKeyRef.current = null;
      return;
    }

    const rawComponentType = String(params.get('componentIdType') || '').trim();
    const parsedComponentIdType =
      rawComponentType === 'summary_component' ||
      rawComponentType === 'fabric' ||
      rawComponentType === 'pg_build_component' ||
      rawComponentType === 'pg_total_only'
        ? rawComponentType
        : null;
    const componentIdType =
      parsedComponentIdType || (hasComponentId && !componentKey ? 'summary_component' : null);

    const deepLinkKey = `${itemId}:${hasComponentId ? componentId : 'none'}:${componentIdType || 'any'}:${componentKey || 'none'}`;
    if (missingComponentDeepLinkKeyRef.current === deepLinkKey) return;

    const rowsForItem = data.filter((row) => Number(row?.order?.id || 0) === itemId);
    if (rowsForItem.length <= 0) {
      missingComponentDeepLinkKeyRef.current = deepLinkKey;
      toast.error('This component is not available anymore.');
      clearNotebookDeepLinkSearchParams();
      return;
    }

    const hasTargetComponent = rowsForItem.some((row) => {
      const rowComponentId = Number(getNotebookComponentId(row) || 0);
      const rowComponentKey = getNotebookComponentKey(row);
      if (hasComponentId && rowComponentId !== componentId) return false;
      if (componentKey && rowComponentKey !== componentKey) return false;
      if (!componentIdType) return true;
      return getNotebookComponentIdType(row) === componentIdType;
    });

    if (hasTargetComponent) {
      missingComponentDeepLinkKeyRef.current = null;
      return;
    }

    missingComponentDeepLinkKeyRef.current = deepLinkKey;
    toast.error('This component is not available anymore.');
    clearNotebookDeepLinkSearchParams();
  }, [loading, hasFetchedTableData, data, locationSearch]);

  const handleInitialFetch = async () => {
    if (includeClientFilter) {
      await handleLoadClients();
    }
    await handleLoadSuppliers();
    handleLoading(false);
  };

  useEffect(() => {
    handleInitialFetch();
  }, [includeClientFilter]);

  useEffect(() => {
    handleGetItemStatuses();
  }, []);

  useEffect(() => {
    if (rowIndex !== null) {
      let foundPoInput = document.getElementById(`po_number_input_row_${rowIndex}`);
      if (!foundPoInput) return;
      foundPoInput.focus();
    }
  }, [rowIndex]);

  const isFiltersActivate =
    (includeClientFilter && filters.client.value && filters.client.value.length > 0) ||
    (filters.supplier.value && filters.supplier.value.length > 0) ||
    (filters.type.value && filters.type.value.length > 0) ||
    (filters.purchasers.value && filters.purchasers.value.length > 0) ||
    (filters.orderingStatus.value && filters.orderingStatus.value.length > 0) ||
    (filters.itemStatus.value && filters.itemStatus.value.length > 0) ||
    // (canUseRoleAssignments && filters.assignee.value && filters.assignee.value.length > 0) ||
    (filters.invoice.value && filters.invoice.value.length > 0);

  const columnFilterProps = {
    value: columnVisibility,
    onChange: handleColumnVisibilityChange,
    showCustomFieldsSection: true,
    canCustomizeCustomColumns,
    initialCustomColumns: customColumnsConfig,
    onSaveCustomColumns: handleSaveCustomColumns,
    hiddenKeys: Array.from(hiddenColumnKeySet),
  };

  return (
    <div className="">
      {showPageHeader && (
        <div className="px-[24px] py-[16px]">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-4">
                <h1 className="m-0 text-[24px] font-semibold text-foreground">
                  {`Ordering & Receiving (${ordersLength || 0})`}
                </h1>
              </div>
              <p className="text-muted-foreground text-sm mt-1">
                Manage all materials, components, and purchased goods for this client.
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Actions */}
      <div className="bg-background shadow-[0_4px_6px_-4px_rgba(0,0,0,0.1)]">
        <div className="pb-4">
          <div className="flex items-center gap-3 flex-wrap">
            {/* Search field */}
            <InputGroup className="max-w-xs min-h-[40px] inline-flex">
              <InputGroupInput
                data-qa={`ordering_and_receiving_search_field`}
                ref={searchInputRef}
                value={searchTerm || ''}
                onChange={(e) => handleChangeValueSearchInput(e)}
                onKeyDown={handleSearchInputKeyDown}
                placeholder="Search..."
              />
              <InputGroupAddon>
                {searchTerm ? (
                  <CirclePlus
                    data-qa={`projects_search_input_clear`}
                    onMouseDown={() => {
                      handleClearSearchKeyword();
                    }}
                    stroke="white"
                    fill="#3b87a0"
                    className="!size-5 text-lg rotate-[45deg] cursor-pointer"
                  />
                ) : (
                  <Search />
                )}
              </InputGroupAddon>
            </InputGroup>

            {/* Filters */}
            {filtersToMap?.map((item: any, index: number) => {
              // let filterOptions = item?.items || [];
              // let chosenFilterValue = filters?.[item?.value]?.value || [];
              // let labelToShow = item?.label;
              // if (chosenFilterValue?.length === 1) {
              //   labelToShow =
              //     filterOptions?.find((fo: any) => fo?.value === chosenFilterValue?.[0])?.label ||
              //     '';
              // } else if (chosenFilterValue?.length > 1) {
              //   labelToShow = `${chosenFilterValue?.length} selected`;
              // }

              return (
                <Fragment key={index}>
                  <Filter
                    shadcn
                    filter_name={item?.label}
                    filter_key={item?.value}
                    filter_values={filters?.[item?.value]?.value}
                    multiple_select={item?.type === 'multi'}
                    items={item?.items || []}
                    archived={false}
                    show_option_all={item?.type === 'single'}
                    handleClearAllFilter={handleClearAllFilter}
                    handleChooseFilter={handleChooseFilter}
                  />
                </Fragment>
              );

              // return (
              //   <Popover key={index}>
              //     <PopoverTrigger className="w-[180px]" asChild>
              //       <Button
              //         type="button"
              //         variant={'outline'}
              //         className={`inline-flex items-center gap-2 whitespace-nowrap rounded-md text-[13px] ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-background hover:border-input-hover h-10 px-4 py-2 w-[180px] justify-between font-normal ${chosenFilterValue?.length > 0 ? 'border-2 border-primary text-primary bg-primary/5' : ''}`}
              //       >
              //         <span className="truncate">
              //           {chosenFilterValue?.length > 0 &&
              //             item?.type === 'multi' &&
              //             `${item?.label}: `}
              //           {labelToShow}
              //         </span>
              //         <LucideChevronDown className="lucide lucide-chevron-down h-4 w-4 shrink-0 opacity-50" />
              //       </Button>
              //     </PopoverTrigger>
              //     <PopoverContent data-side={'bottom'} align={'start'} className="p-[4px]">
              //       <div className="flex flex-col gap-0.5">
              //         {filterOptions?.map((i: any, idx: number) => {
              //           if (item?.type === 'single') {
              //             return (
              //               <Fragment key={idx}>
              //                 <button
              //                   type="button"
              //                   data-qa={`pill_${item?.type}_${i?.id}_item`}
              //                   data-selected={chosenFilterValue?.includes(i?.value)}
              //                   onClick={() => handleChooseFilter(i?.value, item?.value)}
              //                   className={`flex items-center gap-2 w-full text-[13px] pl-[${i?.parentLevel * 7.5}px] pr-2 py-1.5 rounded-sm hover:bg-muted cursor-pointer ${chosenFilterValue?.includes(i?.value) || (chosenFilterValue?.length <= 0 && !i?.value) ? 'bg-primary/10 text-primary font-medium' : ''}`}
              //                   key={idx}
              //                 >
              //                   <LucideCheck
              //                     className={`w-4 h-4 ${chosenFilterValue?.includes(i?.value) || (chosenFilterValue?.length <= 0 && !i?.value) ? 'opacity-100' : 'opacity-0'}`}
              //                   />
              //                   {i?.label}
              //                 </button>
              //               </Fragment>
              //             );
              //           }
              //           return (
              //             <Fragment key={idx}>
              //               <Label className="font-normal flex items-center px-2 py-1.5 rounded-sm hover:bg-muted cursor-pointer">
              //                 <Checkbox
              //                   checked={chosenFilterValue?.includes(i?.value)}
              //                   className="border-primary"
              //                   onCheckedChange={() => handleChooseFilter(i?.value, item?.value)}
              //                 />
              //                 <span className="text-[13px] cursor-pointer flex-1">{i?.label}</span>
              //               </Label>
              //             </Fragment>
              //           );
              //         })}
              //         {item?.type === 'multi' && chosenFilterValue?.length > 0 && (
              //           <>
              //             <div className="border-t border-input my-1" />
              //             <Button
              //               onClick={() => handleClearAllFilter(item?.value)}
              //               type="button"
              //               variant={'clearFilter'}
              //               className="min-h-[36px] text-[13px]"
              //             >
              //               Clear All
              //             </Button>
              //           </>
              //         )}
              //       </div>
              //     </PopoverContent>
              //   </Popover>
              // );
            })}

            {isFiltersActivate && (
              <Button
                data-qa="projects_clear_all_filters"
                variant={'resetFilters'}
                type="button"
                onClick={() => handleResetAllFilters()}
                className="text-[13px]"
              >
                <LucideX />
                Clear All
              </Button>
            )}

            <ColumnVisibilityMenu {...columnFilterProps} />
          </div>
        </div>
      </div>
      {loading ? (
        <div data-qa="overview-loader">
          <FadeLoader
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(50%,-50%)',
              zIndex: 9999,
              display: !loading ? 'none' : 'block',
            }}
            color="#3B97B1"
            aria-label="Loading Spinner"
            data-testid="loader"
          />
        </div>
      ) : (
        <div className={cn('flex-1 min-w-0 min-h-0 overflow-visible pb-6')}>
          <div className="sticky top-0 z-[51] bg-white md:max-w-[calc(100vw-113px-1rem)] max-w-[calc(100vw-77px-1rem)]">
            <Table
              className={cn('table-fixed project-overview-table w-max', 'min-w-full')}
              wrapperRef={headerWrapperRef}
              onWrapperScroll={handleHeaderWrapperScroll}
              wrapperClassName={cn(
                'overflow-x-auto overflow-y-visible max-h-none border-b-0 rounded-b-none'
                // 'scrollbar-hidden'
              )}
            >
              <TableHeader>
                <TableRow className="bg-[#F8FAFC] border-b border-[#D5DDE5]">
                  <TableHead className={cn('px-0 py-0 sticky z-[51] left-0', 'w-[48px]')}>
                    <div className="flex items-center justify-center w-[48px]">
                      <Checkbox
                        aria-label="Select all"
                        name="markAll"
                        data-qa="project-overview-checkbox-all"
                        className="border-primary"
                        // checked={sortedData.length > 0 && selectedRows.length === sortedData.length}
                        checked={data.length > 0 && selectedRows.length === data.length}
                        onCheckedChange={(checked) => handleSelectAllRows(checked === true)}
                      />
                    </div>
                  </TableHead>

                  {!filters?.purchasers?.value?.includes('com') && selectedRowItems.length > 0 ? (
                    <>
                      <TableHead className={cn(`w-[240px] md:sticky md:z-[51]`, 'md:left-[48px]')}>
                        <Popover
                          open={isBatchEditPopoverOpen}
                          onOpenChange={setIsBatchEditPopoverOpen}
                        >
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              className="text-[12px] inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 bg-primary text-primary-foreground hover:bg-primary/90 rounded-[8px] max-h-[30px] p-[10px]"
                            >
                              Batch Actions
                              <span>{`(${selectedRowItems.length || 0})`}</span>
                              <LucideChevronDown />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent align="end" className="min-w-max p-[4px]">
                            <button
                              onClick={() => {
                                setIsBatchEditPopoverOpen(false);
                                void handleBatchDateUpdate('orderedDate');
                              }}
                              type="button"
                              className="w-full cursor-pointer relative flex select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
                            >
                              <LucideShoppingCart className="lucide lucide-package-check h-[16px] w-[16px] shrink-0 mr-2" />
                              Mark as Ordered
                            </button>
                            <button
                              onClick={() => {
                                setIsBatchEditPopoverOpen(false);
                                void handleBatchDateUpdate('receivedDate');
                              }}
                              type="button"
                              className="w-full cursor-pointer relative flex select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
                            >
                              <LucidePackage className="lucide lucide-package-check h-[16px] w-[16px] shrink-0 mr-2" />
                              Mark as Received
                            </button>
                            <button
                              onClick={handleOpenCreatePoModal}
                              type="button"
                              className="w-full cursor-pointer relative flex select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
                            >
                              <LucideFileText className="lucide lucide-package-check h-[16px] w-[16px] shrink-0 mr-2" />
                              Create PO
                            </button>
                          </PopoverContent>
                        </Popover>
                      </TableHead>
                      <TableHead className="w-[160px]"></TableHead>
                      <TableHead className="w-[70px]"></TableHead>
                      <TableHead className="w-[140px]"></TableHead>
                      <TableHead className="w-[96px]"></TableHead>
                    </>
                  ) : (
                    <>
                      {showColumn('supplier') && (
                        <TableHead
                          className={cn(
                            'h-12 px-1.5 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 whitespace-nowrap',
                            'w-[140px]'
                          )}
                        >
                          <div
                            onClick={() => handleSort('supplier')}
                            className="cursor-pointer text-[14px] flex items-center gap-1 hover:text-foreground transition-colors group w-full"
                          >
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="underline">Supplier</span>
                              </TooltipTrigger>
                              <TooltipContent>Sort by supplier</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <LucideArrowUp
                                  data-position={sort?.position}
                                  data-active={sort?.field === 'supplier'}
                                  className="data-[active=false]:hidden data-[active=true]:block data-[position=1]:rotate-180 lucide lucide-arrow-up h-3 w-3"
                                />
                              </TooltipTrigger>
                              <TooltipContent>
                                {sort?.position === 1
                                  ? 'Sort by ascending order'
                                  : 'Sort by descending order'}
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </TableHead>
                      )}
                      {showColumn('category') && (
                        <TableHead
                          className={cn(
                            'h-12 px-1.5 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 whitespace-nowrap',
                            'w-[160px]'
                          )}
                        >
                          <div
                            onClick={() => handleSort('category')}
                            className="cursor-pointer text-[14px] flex items-center gap-1 hover:text-foreground transition-colors group w-full"
                          >
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="underline">
                                  <div className="leading-tight">
                                    <div>Category /</div>
                                    <div>Subcategory</div>
                                  </div>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>Sort by category and subcategory</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <LucideArrowUp
                                  data-position={sort?.position}
                                  data-active={sort?.field === 'category'}
                                  className="data-[active=false]:hidden data-[active=true]:block data-[position=1]:rotate-180 lucide lucide-arrow-up h-3 w-3"
                                />
                              </TooltipTrigger>
                              <TooltipContent>
                                {sort?.position === 1
                                  ? 'Sort by ascending order'
                                  : 'Sort by descending order'}
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </TableHead>
                      )}
                      {showColumn('photo') && (
                        <TableHead
                          className={cn(
                            'h-12 px-1.5 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4',
                            'w-[70px]'
                          )}
                        >
                          <span className="text-[14px]">Photo</span>
                        </TableHead>
                      )}
                      {showColumn('orderingDescription') && (
                        <TableHead
                          className={cn(
                            'h-12 px-0 py-0 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 whitespace-nowrap md:sticky md:z-[51]',
                            'w-[240px]',
                            'md:left-[44px]'
                          )}
                        >
                          <div
                            onClick={() => handleSort('orderingDescription')}
                            className="cursor-pointer text-[14px] py-[1px] px-[6px] flex items-center gap-1 hover:text-foreground transition-colors group w-full h-full md:shadow-[4px_0_6px_-4px_rgba(0,0,0,0.08)]"
                          >
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="underline">Ordering Description</span>
                              </TooltipTrigger>
                              <TooltipContent>Sort by ordering description</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <LucideArrowUp
                                  data-position={sort?.position}
                                  data-active={sort?.field === 'orderingDescription'}
                                  className="data-[active=false]:hidden data-[active=true]:block data-[position=1]:rotate-180 lucide lucide-arrow-up h-3 w-3"
                                />
                              </TooltipTrigger>
                              <TooltipContent>
                                {sort?.position === 1
                                  ? 'Sort by ascending order'
                                  : 'Sort by descending order'}
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </TableHead>
                      )}
                      {showColumn('notebook') && (
                        <TableHead
                          className={cn(
                            'h-12 px-1.5 text-center align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4',
                            'w-[96px]'
                          )}
                        >
                          <span className="text-[14px]">Notebook</span>
                        </TableHead>
                      )}
                    </>
                  )}
                  {showColumn('qty') && (
                    <TableHead
                      className={cn(
                        'h-12 px-1.5 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 whitespace-nowrap',
                        'w-[80px]'
                      )}
                    >
                      <div
                        onClick={() => handleSort('qty')}
                        className="cursor-pointer text-[14px] flex items-center gap-1 hover:text-foreground transition-colors group w-full"
                      >
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="underline">Qty</span>
                          </TooltipTrigger>
                          <TooltipContent>Sort by quantity</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <LucideArrowUp
                              data-position={sort?.position}
                              data-active={sort?.field === 'qty'}
                              className="data-[active=false]:hidden data-[active=true]:block data-[position=1]:rotate-180 lucide lucide-arrow-up h-3 w-3"
                            />
                          </TooltipTrigger>
                          <TooltipContent>
                            {sort?.position === 1
                              ? 'Sort by ascending order'
                              : 'Sort by descending order'}
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </TableHead>
                  )}
                  {showColumn('clientProject') && (
                    <TableHead
                      className={cn(
                        'h-12 px-1.5 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4',
                        'w-[180px]'
                      )}
                    >
                      <div className="text-[14px] flex items-center gap-1">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span
                              onClick={() => handleSort('client')}
                              className={`cursor-pointer ${sort?.field === 'client' ? 'font-semibold underline' : 'font-normal'}`}
                            >
                              Client
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>Sort by client</TooltipContent>
                        </Tooltip>
                        <span className="text-muted-foreground">|</span>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span
                              onClick={() => handleSort('clientParent')}
                              className={`cursor-pointer ${sort?.field === 'clientParent' ? 'font-semibold underline' : 'font-normal'}`}
                            >
                              Parent
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>Sort by parent</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="ml-1 p-0.5 hover:bg-muted rounded transition-colors">
                              <LucideArrowUp
                                data-position={sort?.position}
                                data-active={
                                  sort?.field === 'client' || sort?.field === 'clientParent'
                                }
                                className="data-[active=false]:hidden data-[active=true]:block data-[position=1]:rotate-180 lucide lucide-arrow-up h-3 w-3"
                              />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {sort?.position === 1
                              ? 'Sort by ascending order'
                              : 'Sort by descending order'}
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </TableHead>
                  )}
                  {showColumn('itemRoom') && (
                    <TableHead
                      className={cn(
                        'h-12 px-1.5 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 whitespace-nowrap',
                        'w-[160px]'
                      )}
                    >
                      <div
                        onClick={() => handleSort('itemRoom')}
                        className="cursor-pointer text-[14px] flex items-center gap-1 hover:text-foreground transition-colors group w-full"
                      >
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="underline">Item | Room</span>
                          </TooltipTrigger>
                          <TooltipContent>Sort by item and room</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <LucideArrowUp
                              data-position={sort?.position}
                              data-active={sort?.field === 'itemRoom'}
                              className="data-[active=false]:hidden data-[active=true]:block data-[position=1]:rotate-180 lucide lucide-arrow-up h-3 w-3"
                            />
                          </TooltipTrigger>
                          <TooltipContent>
                            {sort?.position === 1
                              ? 'Sort by ascending order'
                              : 'Sort by descending order'}
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </TableHead>
                  )}
                  {showColumn('wo') && (
                    <TableHead
                      className={cn(
                        'h-12 px-1.5 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4',
                        'w-[80px]'
                      )}
                    >
                      <span className="text-[14px]">WO</span>
                    </TableHead>
                  )}
                  {showColumn('invoice') && (
                    <TableHead
                      className={cn(
                        'h-12 px-1.5 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4',
                        'w-[160px]'
                      )}
                    >
                      <span
                        onClick={() => handleSort('invoice')}
                        className="text-[14px] flex items-center gap-1 whitespace-nowrap"
                      >
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-pointer underline font-medium">Invoice</span>
                          </TooltipTrigger>
                          <TooltipContent>Sort by invoice</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger>
                            <LucideInfo className="lucide lucide-info h-3 w-3 text-muted-foreground shrink-0" />
                          </TooltipTrigger>
                          <TooltipContent>
                            Invoice to the client for this item. Editable on Project Detail page.
                          </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="p-0.5 hover:bg-muted rounded transition-colors">
                              <LucideArrowUp
                                data-position={sort?.position}
                                data-active={sort?.field === 'invoice'}
                                className="data-[active=false]:hidden data-[active=true]:block data-[position=1]:rotate-180 lucide lucide-arrow-up h-3 w-3"
                              />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {sort?.position === 1
                              ? 'Sort by ascending order'
                              : 'Sort by descending order'}
                          </TooltipContent>
                        </Tooltip>
                      </span>
                    </TableHead>
                  )}
                  {showColumn('url') && (
                    <TableHead
                      className={cn(
                        'h-12 px-1.5 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4',
                        'w-[120px]'
                      )}
                    >
                      <span className="text-[14px]">URL</span>
                    </TableHead>
                  )}
                  {showColumn('uploads') && (
                    <TableHead
                      className={cn(
                        'h-12 px-1.5 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4',
                        'w-[140px]'
                      )}
                    >
                      <span className="text-[14px]">Uploads</span>
                    </TableHead>
                  )}
                  {showColumn('purchaser') && (
                    <TableHead
                      className={cn(
                        'h-12 px-1.5 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 whitespace-nowrap',
                        'w-[110px]'
                      )}
                    >
                      <div
                        onClick={() => handleSort('purchaser')}
                        className="cursor-pointer text-[14px] flex items-center gap-1 hover:text-foreground transition-colors group w-full"
                      >
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="underline">Purchaser</span>
                          </TooltipTrigger>
                          <TooltipContent>Sort by purchaser</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <LucideArrowUp
                              data-position={sort?.position}
                              data-active={sort?.field === 'purchaser'}
                              className="data-[active=false]:hidden data-[active=true]:block data-[position=1]:rotate-180 lucide lucide-arrow-up h-3 w-3"
                            />
                          </TooltipTrigger>
                          <TooltipContent>
                            {sort?.position === 1
                              ? 'Sort by ascending order'
                              : 'Sort by descending order'}
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </TableHead>
                  )}
                  {showColumn('itemStatus') && (
                    <TableHead
                      className={cn(
                        'h-12 px-1.5 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 whitespace-nowrap',
                        'w-[120px]'
                      )}
                    >
                      <div
                        onClick={() => handleSort('itemStatus')}
                        className="cursor-pointer text-[14px] flex items-center gap-1 hover:text-foreground transition-colors group w-full"
                      >
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="underline">Item Status</span>
                          </TooltipTrigger>
                          <TooltipContent>Sort by item status</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <LucideArrowUp
                              data-position={sort?.position}
                              data-active={sort?.field === 'itemStatus'}
                              className="data-[active=false]:hidden data-[active=true]:block data-[position=1]:rotate-180 lucide lucide-arrow-up h-3 w-3"
                            />
                          </TooltipTrigger>
                          <TooltipContent>
                            {sort?.position === 1
                              ? 'Sort by ascending order'
                              : 'Sort by descending order'}
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </TableHead>
                  )}
                  {showColumn('installDate') && (
                    <TableHead
                      className={cn(
                        'h-12 px-1.5 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4',
                        'w-[130px]'
                      )}
                    >
                      <span
                        onClick={() => handleSort('installDate')}
                        className="text-[14px] flex items-center gap-1 whitespace-nowrap"
                      >
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-pointer underline font-medium">
                              Install Date
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>Sort by install date</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger>
                            <LucideInfo className="lucide lucide-info h-3 w-3 text-muted-foreground shrink-0" />
                          </TooltipTrigger>
                          <TooltipContent>Pulled from Item's Install Date</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="p-0.5 hover:bg-muted rounded transition-colors">
                              <LucideArrowUp
                                data-position={sort?.position}
                                data-active={sort?.field === 'installDate'}
                                className="data-[active=false]:hidden data-[active=true]:block data-[position=1]:rotate-180 lucide lucide-arrow-up h-3 w-3"
                              />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {sort?.position === 1
                              ? 'Sort by ascending order'
                              : 'Sort by descending order'}
                          </TooltipContent>
                        </Tooltip>
                      </span>
                    </TableHead>
                  )}
                  {showColumn('dueDate') && (
                    <TableHead
                      className={cn(
                        'h-12 px-1.5 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4',
                        'w-[130px]'
                      )}
                    >
                      <span
                        onClick={() => handleSort('dueDate')}
                        className="text-[14px] flex items-center gap-1 whitespace-nowrap"
                      >
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-pointer underline font-medium">Due Date</span>
                          </TooltipTrigger>
                          <TooltipContent>Sort by due date</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger>
                            <LucideInfo className="lucide lucide-info h-3 w-3 text-muted-foreground shrink-0" />
                          </TooltipTrigger>
                          <TooltipContent>Pulled from Item's Production Due Date</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="p-0.5 hover:bg-muted rounded transition-colors">
                              <LucideArrowUp
                                data-position={sort?.position}
                                data-active={sort?.field === 'dueDate'}
                                className="data-[active=false]:hidden data-[active=true]:block data-[position=1]:rotate-180 lucide lucide-arrow-up h-3 w-3"
                              />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {sort?.position === 1
                              ? 'Sort by ascending order'
                              : 'Sort by descending order'}
                          </TooltipContent>
                        </Tooltip>
                      </span>
                    </TableHead>
                  )}
                  {visibleCustomColumns.map((customColumn) => {
                    const visibilityKey =
                      CUSTOM_COLUMN_VISIBILITY_KEY_BY_ID[customColumn.id as CustomColumnId];
                    if (!showColumn(visibilityKey)) {
                      return null;
                    }
                    return (
                      <TableHead
                        key={`custom-column-head-${customColumn.id}`}
                        className={cn(
                          'h-12 px-1.5 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4',
                          'w-[170px] min-w-[170px] max-w-[170px]'
                        )}
                      >
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="w-[150px] overflow-hidden">
                              <span className="block w-full truncate text-[14px] cursor-default">
                                {customColumn.name}
                              </span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>{customColumn.name}</TooltipContent>
                        </Tooltip>
                      </TableHead>
                    );
                  })}
                  {showColumn('orderedDate') && (
                    <TableHead
                      className={cn(
                        'h-12 px-0 py-0 text-left align-middle font-medium text-muted-foreground md:sticky md:z-[51]',
                        'w-[140px]',
                        showColumn('receivedDate') ? 'md:right-[195px]' : 'md:right-[55px]'
                      )}
                    >
                      <div className="text-[14px] leading-tight font-medium text-muted-foreground h-full py-[1px] px-[6px] grid md:shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.08)]">
                        <div
                          onClick={() => handleSort('orderedDate')}
                          className="flex items-center gap-1 w-max"
                        >
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-pointer underline w-max">Ordered</span>
                            </TooltipTrigger>
                            <TooltipContent>Sort by ordered status</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="p-0.5 hover:bg-muted rounded transition-colors">
                                <LucideArrowUp
                                  data-position={sort?.position}
                                  data-active={sort?.field === 'orderedDate'}
                                  className="data-[active=false]:hidden data-[active=true]:block data-[position=1]:rotate-180 lucide lucide-arrow-up h-3 w-3"
                                />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              {sort?.position === 1
                                ? 'Sort by ascending order'
                                : 'Sort by descending order'}
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <div>& PO #</div>
                      </div>
                    </TableHead>
                  )}
                  {showColumn('receivedDate') && (
                    <TableHead
                      className={cn(
                        'h-12 px-0 py-0 text-left align-middle font-medium text-muted-foreground md:sticky md:right-[55px] md:z-[51]',
                        'w-[140px]'
                      )}
                    >
                      <div
                        className={cn(
                          'text-[14px] leading-tight h-full py-[1px] px-[6px] font-medium text-muted-foreground',
                          !showColumn('orderedDate')
                            ? 'md:shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.08)]'
                            : ''
                        )}
                      >
                        <div
                          onClick={() => handleSort('receivedDate')}
                          className="flex items-center gap-1 w-max"
                        >
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-pointer underline w-max">Received</span>
                            </TooltipTrigger>
                            <TooltipContent>Sort by received status</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="p-0.5 hover:bg-muted rounded transition-colors">
                                <LucideArrowUp
                                  data-position={sort?.position}
                                  data-active={sort?.field === 'receivedDate'}
                                  className="data-[active=false]:hidden data-[active=true]:block data-[position=1]:rotate-180 lucide lucide-arrow-up h-3 w-3"
                                />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              {sort?.position === 1
                                ? 'Sort by ascending order'
                                : 'Sort by descending order'}
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <div
                          onClick={() => handleSort('etaDate')}
                          className="flex items-center gap-1"
                        >
                          <span>&</span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-pointer underline w-max">ETA date</span>
                            </TooltipTrigger>
                            <TooltipContent>Sort by ETA date</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="p-0.5 hover:bg-muted rounded transition-colors">
                                <LucideArrowUp
                                  data-position={sort?.position}
                                  data-active={sort?.field === 'etaDate'}
                                  className="data-[active=false]:hidden data-[active=true]:block data-[position=1]:rotate-180 lucide lucide-arrow-up h-3 w-3"
                                />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              {sort?.position === 1
                                ? 'Sort by ascending order'
                                : 'Sort by descending order'}
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      </div>
                    </TableHead>
                  )}

                  <TableHead>
                    {props.backToTop && (
                      <div className="flex items-center justify-center">
                        <button
                          data-qa={`back_to_top_arrow`}
                          onClick={() => {
                            if (props.pageScrollTo) {
                              props.pageScrollTo('top', 0);
                            }
                          }}
                          type="button"
                          className="text-white w-8 h-8 rounded-full bg-teal text-teal-foreground flex items-center justify-center shadow-md hover:bg-teal-dark transition-colors animate-fade-in mx-auto"
                        >
                          <ArrowUp className="lucide lucide-arrow-up h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </TableHead>
                </TableRow>
              </TableHeader>
            </Table>
          </div>

          <Table
            className={cn('table-fixed project-overview-table', 'w-max min-w-full')}
            wrapperRef={bodyWrapperRef}
            onWrapperScroll={handleBodyWrapperScroll}
            wrapperClassName={cn(
              '!pb-[40px] md:h-full h-full md:max-h-full max-h-full overflow-x-auto overflow-y-visible overscroll-x-contain overscroll-y-auto max-h-none border-t-0 rounded-t-none z-[50]'
            )}
          >
            <TableBody className="[&_tr:last-child]:border-b">
              {data &&
                data?.length > 0 &&
                data?.map((d: any, dataIdx: number) => {
                  // mapping
                  let hasPoGenerated =
                    d?.component?.poGenerated ||
                    d?.component?.draperyOrderSummaryComponent?.poGenerated ||
                    false;

                  let handleDetermineSupplier = () => {
                    if (d?.order?.orderType !== 'purchased-goods') {
                      let supplierObj = null;
                      switch (d?.source) {
                        case 'drapery-fabric':
                          if (isNaN(d?.componentType)) {
                            supplierObj =
                              d?.hardware?.supplierObj || d?.headrail?.supplierObj || null;
                          } else {
                            supplierObj = d?.component?.supplier || null;
                          }
                          break;
                        case 'drapery-summary-component':
                          if (d?.component?.draperyOrderSummaryComponent?.isStaticHardware) {
                            supplierObj =
                              d?.hardware?.supplierObj || d?.headrail?.supplierObj || null;
                          }
                          break;
                        default:
                          supplierObj = null;
                          break;
                      }
                      return supplierObj;
                    } else if (d?.order?.orderType === 'purchased-goods') {
                      return d?.order?.purchasedGoodsSupplier || null;
                    } else {
                      return null;
                    }
                  };

                  let supplierItem = handleDetermineSupplier();

                  const purchaserDisplayName = () => {
                    if (d?.order?.orderType !== 'purchased-goods') {
                      let orderOrigin = 0;
                      switch (d?.source) {
                        case 'drapery-fabric':
                          orderOrigin =
                            d?.component?.orderOrigin ||
                            d?.hardware?.purchaser ||
                            d?.headrail?.purchaser ||
                            0;
                          break;
                        case 'drapery-summary-component':
                          orderOrigin = d?.hardware?.purchaser || d?.headrail?.purchaser || 0;
                          break;
                        default:
                          orderOrigin = 0;
                          break;
                      }
                      return orderOrigin === 2 ? 'COM' : orderOrigin === 1 ? 'Workroom' : '-';
                    } else if (d?.order?.orderType === 'purchased-goods') {
                      let pgOrderOrigin = d?.order?.purchasedGoodsPurchaser ?? 0;
                      return pgOrderOrigin === 2 ? 'COM' : pgOrderOrigin === 1 ? 'Workroom' : '-';
                    } else {
                      return '-';
                    }
                  };

                  const itemDisplayName =
                    d?.order?.item?.name ||
                    d?.order?.itemType?.name ||
                    d?.order?.purchasedGoodsItemType?.name ||
                    'Drapery';
                  const windowOrLocation = () => {
                    if (d?.order?.orderType === 'custom-order') {
                      return d?.order?.locationId || '';
                    } else if (d?.order?.orderType !== 'purchased-goods') {
                      return d?.order?.window || '';
                    } else {
                      return d?.order?.purchasedGoodsWindowLocationId || '';
                    }
                  };
                  const itemRoomDisplayName = d?.order?.room || d?.order?.roomName || '';
                  const workOrderValue = d?.order?.workOrder?.num || null;
                  const orderId = d?.order?.id || null;
                  const clientDisplayName =
                    d?.order?.client?.displayName ||
                    `${d?.order?.client?.firstName} ${d?.order?.client?.lastName}` ||
                    '-';
                  const parentClientId = d?.order?.client?.parentClient?.id ?? null;
                  const parentClientDisplayName =
                    d?.order?.client?.parentClient?.displayName ?? null;

                  const orderSummary = d?.order?.draperyOrderSummary;
                  const installationJson = orderSummary?.installationJson || null;
                  const productionJson = orderSummary?.productionJson || null;
                  const invoiceJson = orderSummary?.invoiceJson || null;
                  const invoiceStatus = invoiceJson?.paid || null;
                  const invoiceNumber = invoiceJson?.num || null;
                  const invoiceDate = invoiceJson?.invoiceDateSent
                    ? moment(new Date(invoiceJson?.invoiceDateSent))?.format('MM/DD/YY')
                    : null;

                  const orderUploads = d?.uploads || [];
                  const component = d?.component;
                  const draperyOrderSummaryComponent = component?.draperyOrderSummaryComponent;

                  const orderedDateValue =
                    component?.orderedDate ||
                    draperyOrderSummaryComponent?.orderedDate ||
                    undefined;
                  const receivedDateValue =
                    component?.receivedDate ||
                    draperyOrderSummaryComponent?.receivedDate ||
                    undefined;
                  const etaDateValue =
                    component?.eta || draperyOrderSummaryComponent?.eta || undefined;
                  const poNumber = component?.po || draperyOrderSummaryComponent?.po || '';
                  const patternObj = component?.patternObj || component?.item || null;
                  const isMyShopItem = Boolean(patternObj?.id);
                  const changePhotoInputId = `change-photo-${dataIdx}`;
                  const uploadPhotoInputId = `upload-photo-${dataIdx}`;

                  const componentQuantity = () => {
                    if (d?.order?.orderType !== 'purchased-goods') {
                      let quantity = '-';
                      switch (d?.source) {
                        case 'drapery-fabric':
                          if (isNaN(d?.componentType)) {
                            quantity =
                              d?.hardware?.hardwareQuantity || d?.headrail?.quantity || '-';
                          } else {
                            if (d?.component?.yardsNeeded) {
                              quantity = `${component?.yardsNeeded} yards`;
                            } else {
                              quantity =
                                d?.component?.qty ||
                                d?.component?.draperyOrderSummaryComponent?.qty ||
                                '-';
                            }
                          }
                          break;
                        case 'drapery-summary-component':
                          if (d?.component?.draperyOrderSummaryComponent?.isStaticHardware) {
                            quantity =
                              d?.hardware?.hardwareQuantity || d?.headrail?.quantity || '-';
                          }
                          break;
                        default:
                          quantity = '-';
                          break;
                      }
                      return quantity;
                    } else if (d?.order?.orderType === 'purchased-goods') {
                      if (d?.order?.purchasedGoodsComponentsType === 'components-build') {
                        return d?.component?.qty || '-';
                      } else {
                        return (
                          d?.order?.purchasedGoodsComponents?.find(
                            (f: any) => f.type === 'totalOnly'
                          )?.qty || '-'
                        );
                      }
                    } else {
                      return '-';
                    }
                  };

                  const determineNonMyShopItemDescription = () => {
                    if (d?.order?.orderType === 'purchased-goods') {
                      if (d?.order?.purchasedGoodsComponentsType === 'components-build') {
                        return d?.component?.item?.orderingDescription || '-';
                      } else {
                        let foundComponent = d?.order?.purchasedGoodsComponents?.find(
                          (f: any) => f.type === 'totalOnly'
                        );
                        if (foundComponent) {
                          return foundComponent?.estimateDescription || '-';
                        }
                        return '-';
                      }
                    } else {
                      let desc = '-';
                      switch (d?.source) {
                        case 'drapery-fabric':
                          if (isNaN(d?.componentType)) {
                            desc =
                              d?.hardware?.hardwareDescription ||
                              d?.headrail?.estimateDescription ||
                              '-';
                          } else {
                            desc =
                              d?.component?.patternObj?.orderingDescription ||
                              d?.component?.pattern ||
                              '-';
                          }
                          break;
                        case 'drapery-summary-component':
                          if (d?.component?.draperyOrderSummaryComponent?.isStaticHardware) {
                            desc =
                              d?.hardware?.hardwareDescription ||
                              d?.headrail?.estimateDescription ||
                              '-';
                          }
                          break;
                        default:
                          desc = '-';
                          break;
                      }
                      return desc;
                    }
                  };

                  const orderingDescriptionValue = isMyShopItem
                    ? patternObj?.orderingDescription || '-'
                    : determineNonMyShopItemDescription();
                  const componentNameValueRaw =
                    normalizeCategoryDisplayText(
                      d?.component?.draperyOrderSummaryComponent?.item
                    ) ||
                    normalizeCategoryDisplayText(d?.component?.item?.name) ||
                    normalizeCategoryDisplayText(d?.component?.patternObj?.name) ||
                    normalizeCategoryDisplayText(d?.component?.pattern);
                  const componentOrderingDescriptionValueRaw = String(
                    orderingDescriptionValue || ''
                  ).trim();
                  const componentNameValue =
                    componentNameValueRaw && componentNameValueRaw !== '-'
                      ? componentNameValueRaw
                      : '';
                  const componentOrderingDescriptionValue =
                    componentOrderingDescriptionValueRaw &&
                    componentOrderingDescriptionValueRaw !== '-'
                      ? componentOrderingDescriptionValueRaw
                      : '';

                  const { category, subcategory } = getCategoryData(d);

                  return (
                    <TableRow
                      key={dataIdx}
                      className={cn(
                        'bg-white border-b border-border/60 project-overview-row transition-colors',
                        '[&>td]:bg-white [&:hover>td]:bg-[#F8FAFC] hover:bg-[#F8FAFC]'
                      )}
                    >
                      <TableCell className="px-0 py-0 sticky z-[50] left-0 w-[48px]">
                        <div className="flex items-center justify-center w-[48px]">
                          <Checkbox
                            className="border-primary"
                            aria-label="Select row"
                            checked={selectedRowSet.has(getRowSelectionKey(d, dataIdx))}
                            onCheckedChange={() => handleSelectRows(d, dataIdx)}
                          />
                        </div>
                      </TableCell>

                      {showColumn('supplier') && (
                        <TableCell className={cn('p-0 w-[140px]')}>
                          <div className={`min-h-[70px] px-[6px] py-0 flex items-center`}>
                            {supplierItem?.id ? (
                              <button
                                onClick={() => handleOpenSupplierModal(true, supplierItem)}
                                type="button"
                                className="inline-flex items-center justify-center gap-2 rounded-md text-sm ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 text-primary underline-offset-4 hover:underline h-auto p-0 text-left font-normal wrap-text leading-snug"
                              >
                                {supplierItem?.displayName}
                              </button>
                            ) : (
                              <span className="text-sm text-muted-foreground">-</span>
                            )}
                          </div>
                        </TableCell>
                      )}
                      {showColumn('category') && (
                        <TableCell className={cn('p-0 w-[160px]')}>
                          <div className={`min-h-[70px] px-[6px] py-0 flex items-center`}>
                            <div>
                              <div
                                className={cn(
                                  'text-sm wrap-text',
                                  category?.preserveCase ? '' : 'capitalize'
                                )}
                              >
                                {category?.preserveCase
                                  ? category?.name
                                  : category?.name?.toLowerCase() ||
                                    category?.item?.toLowerCase() ||
                                    '-'}
                              </div>
                              <div className="text-xs text-muted-foreground wrap-text">
                                {subcategory?.name || '-'}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                      )}
                      {showColumn('photo') && (
                        <TableCell className={cn('p-0 w-[70px]')}>
                          <div className={`min-h-[70px] px-[6px] py-0 flex items-center`}>
                            {patternObj && (
                              <Popover>
                                <PopoverTrigger asChild>
                                  {patternObj?.avatarId ? (
                                    <button
                                      type="button"
                                      className="flex items-center justify-center w-8 h-8 rounded-md border border-solid border-border hover:border-primary/60 transition-colors overflow-hidden"
                                    >
                                      <img
                                        className="w-full h-full object-cover"
                                        src={
                                          patternObj?.avatarId
                                            ? resolveImageSrc(patternObj?.avatarId)
                                            : KravetPlaceholderImage
                                        }
                                      />
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      className="flex items-center justify-center w-8 h-8 rounded-md border border-dashed border-muted-foreground/40 hover:border-primary/60 transition-colors overflow-hidden"
                                    >
                                      <LucideUpload className="lucide lucide-upload w-4 h-4 text-muted-foreground" />
                                    </button>
                                  )}
                                </PopoverTrigger>
                                <PopoverContent className="w-[256px]">
                                  <div className="space-y-3">
                                    <h4 className="m-0 font-medium text-sm">Photo</h4>
                                    {patternObj?.avatarId ? (
                                      <>
                                        <div className="relative">
                                          <img
                                            src={
                                              patternObj?.avatarId
                                                ? resolveImageSrc(patternObj?.avatarId)
                                                : KravetPlaceholderImage
                                            }
                                            className="w-full h-32 object-cover rounded border"
                                          />
                                        </div>
                                        <input
                                          id={changePhotoInputId}
                                          disabled={isUploadingImage}
                                          type="file"
                                          accept="image/*"
                                          className="hidden"
                                          onChange={(e) => handleImageFileChange(e, d)}
                                        />
                                        <div className="flex gap-2">
                                          <Label
                                            htmlFor={changePhotoInputId}
                                            data-disabled={isUploadingImage}
                                            className="min-h-[36px] cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-background hover:border-input-hover h-9 rounded-md px-3 flex-1"
                                          >
                                            <LucideUpload className="lucide lucide-upload w-3.5 h-3.5 mr-1.5" />
                                            Replace
                                          </Label>
                                          <button
                                            disabled={isUploadingImage}
                                            onClick={() => handlePreviewInNewTab(patternObj)}
                                            type="button"
                                            className="min-h-[32px] inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-accent h-8 w-8 text-muted-foreground hover:text-foreground"
                                          >
                                            <LucideEye className="lucide lucide-eye w-3.5 h-3.5" />
                                          </button>
                                          <Button
                                            disabled={isUploadingImage}
                                            className="min-h-[32px]"
                                            type="button"
                                            variant={'trash-button'}
                                            onClick={() => handleDeleteItemImage(d)}
                                          >
                                            <LucideTrash2 className="lucide lucide-trash2 w-3.5 h-3.5" />
                                          </Button>
                                        </div>
                                      </>
                                    ) : (
                                      <>
                                        <input
                                          id={uploadPhotoInputId}
                                          disabled={isUploadingImage}
                                          type="file"
                                          accept="image/*"
                                          className="hidden"
                                          onChange={(e) => handleImageFileChange(e, d)}
                                        />
                                        <div className="flex gap-2">
                                          <Label
                                            htmlFor={uploadPhotoInputId}
                                            data-disabled={isUploadingImage}
                                            className="cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-background hover:border-input-hover h-9 rounded-md px-3 flex-1"
                                          >
                                            <LucideUpload className="lucide lucide-upload w-3.5 h-3.5 mr-1.5" />
                                            {isUploadingImage ? 'Uploading...' : 'Upload'}
                                          </Label>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                </PopoverContent>
                              </Popover>
                            )}
                          </div>
                        </TableCell>
                      )}
                      {showColumn('orderingDescription') && (
                        <TableCell
                          className={cn('p-0 p-0 w-[240px]', 'md:sticky md:z-[50] md:left-[44px]')}
                        >
                          <div className="text-[14px] min-h-[70px] whitespace-normal break-words leading-snug px-[6px] py-0 h-full md:shadow-[4px_0_6px_-4px_rgba(0,0,0,0.08)] flex items-center">
                            {isMyShopItem ? (
                              <button
                                type="button"
                                onClick={() => handleOpenMyShopItemWideSheet(patternObj)}
                                className="cursor-pointer inline-flex items-center justify-center gap-2 rounded-md text-sm ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 text-primary underline-offset-4 hover:underline h-auto p-0 text-left font-medium wrap-text leading-snug"
                              >
                                {orderingDescriptionValue}
                              </button>
                            ) : (
                              <span className="wrap-text leading-snug">
                                {orderingDescriptionValue}
                              </span>
                            )}
                          </div>
                        </TableCell>
                      )}
                      {showColumn('notebook') && (
                        <TableCell className={cn('p-0 w-[96px]')}>
                          <div className="min-h-[70px] px-[6px] py-0 flex items-center justify-center">
                            <Notebook
                              row={{
                                id: d?.order?.id || null,
                                client: d?.order?.client || null,
                                orderType: d?.order?.orderType || null,
                                purchasedGoodsItemType: d?.order?.purchasedGoodsItemType || null,
                                itemType: d?.order?.itemType || null,
                                roomName: d?.order?.roomName || d?.order?.room?.name || '',
                                notebook: d?.order?.notebook || [],
                              }}
                              defaultTab={hasNotebookComponentScope(d) ? 'components' : 'items'}
                              notebookTabs={
                                hasNotebookComponentScope(d)
                                  ? ['projects', 'items', 'components']
                                  : ['projects', 'items']
                              }
                              user={props.accountUser}
                              table={'items'}
                              canAccessTeamSettings={props.canAccessTeamSettings}
                              componentContext={{
                                componentId: getNotebookComponentId(d),
                                componentIdType: getNotebookComponentIdType(d),
                                componentKey: getNotebookComponentKey(d),
                                componentName: componentNameValue,
                                componentOrderingDescription: componentOrderingDescriptionValue,
                              }}
                              triggerClassName={'h-8 w-8'}
                              closeOnOutsideClick={false}
                            />
                          </div>
                        </TableCell>
                      )}
                      {showColumn('qty') && (
                        <TableCell className={cn('p-0 w-[80px]')}>
                          <div
                            className={`min-h-[70px] px-[6px] py-0 flex items-center whitespace-normal break-words break-all`}
                          >
                            {componentQuantity()}
                          </div>
                        </TableCell>
                      )}
                      {showColumn('clientProject') && (
                        <TableCell className={cn('p-0 w-[180px]')}>
                          <div className={`min-h-[70px] px-[6px] py-0 flex items-center`}>
                            <div>
                              <div
                                onClick={() => handleVisitClient(d?.order?.client?.id || null)}
                                className="text-[14px] font-medium text-teal cursor-pointer hover:underline wrap-text"
                              >
                                {clientDisplayName || ''}
                              </div>
                              {parentClientDisplayName ? (
                                <div
                                  onClick={() => {
                                    if (parentClientId == null) return;
                                    handleVisitClient(parentClientId);
                                  }}
                                  className="text-sm text-muted-foreground cursor-pointer hover:underline wrap-text"
                                >
                                  {parentClientDisplayName}
                                </div>
                              ) : (
                                <div className="text-sm text-muted-foreground">-</div>
                              )}
                            </div>
                          </div>
                        </TableCell>
                      )}
                      {showColumn('itemRoom') && (
                        <TableCell className={cn('p-0 w-[160px]')}>
                          <div className={`min-h-[70px] px-[6px] py-0 flex items-center`}>
                            <div>
                              <button
                                onClick={() => openOrderFromItemRoom(d, clientDisplayName)}
                                type="button"
                                className="inline-flex items-center justify-center gap-2 rounded-md text-sm ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 text-primary underline-offset-4 hover:underline h-auto p-0 text-left font-normal wrap-text"
                              >
                                {`${itemDisplayName}${windowOrLocation() ? ` / ${windowOrLocation()}` : ''}`}
                              </button>
                              <div className="text-sm text-muted-foreground wrap-text">
                                {/* <button
                                  onClick={() => openOrderFromItemRoom(d, clientDisplayName)}
                                  type="button"
                                  className="text-sm text-muted-foreground whitespace-normal break-words hover:underline p-0 bg-transparent border-0 cursor-pointer text-left"
                                > */}
                                {itemRoomDisplayName}
                                {/* </button> */}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                      )}
                      {showColumn('wo') && (
                        <TableCell className={cn('p-0 w-[80px]')}>
                          <div className={`min-h-[70px] px-[6px] py-0 flex items-center`}>
                            {workOrderValue ? (
                              <div
                                onClick={() => handleProjectWorkOrderNumClicked(orderId)}
                                className="text-[14px] text-teal font-medium underline hover:text-teal/80 cursor-pointer whitespace-normal break-words break-all"
                              >
                                {workOrderValue || '-'}
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </div>
                        </TableCell>
                      )}
                      {showColumn('invoice') && (
                        <TableCell className={cn('p-0 w-[160px]')}>
                          <div className={`min-h-[70px] px-[6px] py-0 flex items-center`}>
                            {invoiceStatus || invoiceNumber || invoiceDate ? (
                              <div className="space-y-0.5">
                                {invoiceStatus && (
                                  <div className="leading-[1rem] inline-flex items-center rounded-full border border-input px-[10px] py-[2px] transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 text-foreground text-[12px] font-normal wrap-text">
                                    {invoiceStatus}
                                  </div>
                                )}
                                {(invoiceNumber || invoiceDate) && (
                                  <div className="text-sm text-muted-foreground whitespace-normal break-words">
                                    <span className="min-w-0 whitespace-normal break-words break-all">
                                      {invoiceNumber ? `#${invoiceNumber}` : ''}
                                    </span>
                                    <span className="">
                                      {invoiceDate ? ` · ${invoiceDate}` : ''}
                                    </span>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </div>
                        </TableCell>
                      )}
                      {showColumn('url') && (
                        <TableCell className={cn('p-0 w-[120px]')}>
                          <div className={`min-h-[70px] px-[6px] py-0 flex items-center`}>
                            <div>
                              {patternObj?.url ? (
                                <a
                                  href={normalizeUrl(patternObj.url)}
                                  target="_blank"
                                  className="text-primary hover:text-primary/80 text-sm inline-flex items-center gap-1"
                                >
                                  Link{' '}
                                  <LucideExternalLink className="lucide lucide-external-link h-3.5 w-3.5" />
                                </a>
                              ) : (
                                <span className="text-sm text-muted-foreground">-</span>
                              )}
                            </div>
                          </div>
                        </TableCell>
                      )}
                      {showColumn('uploads') && (
                        <TableCell className={cn('p-0 w-[140px]')}>
                          <div className={`min-h-[70px] px-[6px] py-0 flex items-center`}>
                            <Popover
                              open={uploadIndex === dataIdx}
                              onOpenChange={(open) => setUploadIndex(open ? dataIdx : null)}
                            >
                              <PopoverTrigger asChild>
                                <button
                                  type="button"
                                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-accent hover:text-accent-foreground rounded-md h-8 p-0 min-w-8"
                                >
                                  {orderUploads?.length > 0 ? (
                                    <LucideFileText className="lucide lucide-file-text h-4 w-4 text-primary" />
                                  ) : (
                                    <LucideUpload className="lucide lucide-upload h-4 w-4 text-muted-foreground" />
                                  )}
                                </button>
                              </PopoverTrigger>
                              <PopoverContent className="w-[320px]">
                                <div className="space-y-3">
                                  <div className="flex items-center justify-between">
                                    <h4 className="m-0 font-medium text-sm">Documents</h4>
                                    <button
                                      onClick={() => setUploadIndex(null)}
                                      type="button"
                                      className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-accent hover:text-accent-foreground rounded-md h-6 w-6 p-0"
                                    >
                                      <LucideX className="lucide lucide-x h-4 w-4" />
                                    </button>
                                  </div>

                                  {orderUploads?.length > 0 && (
                                    <div>
                                      <p className="text-xs font-medium text-muted-foreground mb-2">
                                        Uploaded documents:
                                      </p>
                                      <div className="space-y-1.5 max-h-48 overflow-y-auto">
                                        {orderUploads?.map((upload: any, uploadIndex: number) => {
                                          const fileNamePopup = upload?.source
                                            ? upload.source.substring(
                                                upload.source.lastIndexOf('/') + 1
                                              )
                                            : '';
                                          return (
                                            <div
                                              key={uploadIndex}
                                              className="flex items-center justify-between gap-2 p-2 bg-muted/50 rounded-md"
                                            >
                                              <button
                                                disabled={disabledDoc}
                                                onClick={() =>
                                                  handleDownloadUploadDoc(upload?.source)
                                                }
                                                className="flex items-center gap-2 min-w-0 flex-1 text-left hover:text-primary transition-colors"
                                                type="button"
                                              >
                                                <LucideFileText className="lucide lucide-file-text h-4 w-4 text-primary shrink-0" />
                                                <span className="text-sm truncate">
                                                  {fileNamePopup}
                                                </span>
                                              </button>
                                              <Button
                                                disabled={disabledDoc}
                                                onClick={() =>
                                                  handleDeleteUpload(upload, orderUploads, d)
                                                }
                                                className="p-0 min-h-[24px] max-h-[24px] h-[24px]"
                                                type="button"
                                                variant={'trash-button'}
                                              >
                                                <LucideTrash2 className="lucide lucide-trash2 h-3 w-3" />
                                              </Button>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}

                                  <Label
                                    data-disabled={disabledDoc}
                                    onDragOver={(e) => {
                                      e.preventDefault();
                                    }}
                                    onDrop={(e) => handleDrop(e, d)}
                                    htmlFor="upload-doc"
                                    className={`flex-col gap-0 border-2 border-dashed rounded-md p-4 text-center cursor-pointer transition-colors border-muted-foreground/25 hover:border-muted-foreground/50`}
                                  >
                                    <LucideUpload className="lucide lucide-upload h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                                    <p className="text-xs text-muted-foreground font-normal">
                                      Drag & drop files here, or click to browse
                                    </p>
                                  </Label>

                                  {uploadError && (
                                    <div className="mt-3 w-full">
                                      <UploadErrorBanner centered message={uploadError} />
                                    </div>
                                  )}

                                  <div>
                                    <input
                                      disabled={disabledDoc}
                                      id="upload-doc"
                                      type="file"
                                      accept="image/*,application/pdf"
                                      multiple
                                      className="hidden"
                                      onChange={(e) => handleChangeFile(e, d)}
                                    />
                                    <Label
                                      data-disabled={disabledDoc}
                                      htmlFor="upload-doc"
                                      className="cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-background hover:border-input-hover h-9 rounded-md px-3 w-full"
                                    >
                                      <LucideUpload className="lucide lucide-upload h-4 w-4 mr-2" />
                                      {disabledDoc ? 'Uploading...' : 'Upload document'}
                                    </Label>
                                  </div>
                                </div>
                              </PopoverContent>
                            </Popover>
                          </div>
                        </TableCell>
                      )}
                      {showColumn('purchaser') && (
                        <TableCell className={cn('p-0 w-[110px]')}>
                          <div
                            className={`text-[14px] min-h-[70px] px-[6px] py-0 flex items-center`}
                          >
                            <div className="whitespace-normal break-words break-all">
                              {purchaserDisplayName()}
                            </div>
                          </div>
                        </TableCell>
                      )}
                      {showColumn('itemStatus') && (
                        <TableCell className={cn('p-0 w-[120px]')}>
                          <div className={`min-h-[70px] px-[6px] py-0 flex items-center`}>
                            <div
                              // type="button"
                              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md text-sm ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:text-accent-foreground h-auto p-0 justify-start font-normal hover:bg-transparent"
                            >
                              <div
                                className={`font-medium flex items-center gap-1 text-sm ${handleCellColor(d?.order?.status || '')} wrap-text`}
                              >
                                {d?.order?.status || ''}
                                {/* <LucideChevronDown className="lucide lucide-chevron-down w-3 h-3" /> */}
                              </div>
                            </div>
                            {/* <Popover>
                              <PopoverTrigger asChild>
                              </PopoverTrigger>
                              <PopoverContent align={'start'} className="p-[4px] w-[192px]">
                                <div>
                                  {itemStatuses?.map((initial: any, initialIndex: number) => {
                                    return (
                                      <div
                                        key={initialIndex}
                                        // onClick={() => handleChangeItemStatus(initial?.value, d)}
                                        className={`relative flex select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 table_gray hover:bg-accent hover:text-accent-foreground ${handleCellColor(initial?.value)}`}
                                      >
                                        {initial?.label || initial?.name}
                                      </div>
                                    );
                                  })}
                                </div>
                              </PopoverContent>
                            </Popover> */}
                          </div>
                        </TableCell>
                      )}
                      {showColumn('installDate') && (
                        <TableCell className={cn('p-0 w-[130px]')}>
                          <div className={`min-h-[70px] px-[6px] py-0 flex items-center`}>
                            <div className="text-[14px] text-muted-foreground">
                              {installationJson?.dateOfInstall
                                ? moment(installationJson?.dateOfInstall).format('MM/DD/YY')
                                : `-`}
                            </div>
                          </div>
                        </TableCell>
                      )}
                      {showColumn('dueDate') && (
                        <TableCell className={cn('p-0 w-[130px]')}>
                          <div className={`min-h-[70px] px-[6px] py-0 flex items-center`}>
                            <div className="text-[14px] text-muted-foreground">
                              {productionJson?.dueDate
                                ? moment(productionJson?.dueDate).format('MM/DD/YY')
                                : `-`}
                            </div>
                          </div>
                        </TableCell>
                      )}
                      {visibleCustomColumns.map((customColumn) => {
                        const customColumnId = customColumn.id as CustomColumnId;
                        const visibilityKey = CUSTOM_COLUMN_VISIBILITY_KEY_BY_ID[customColumnId];
                        if (!showColumn(visibilityKey)) {
                          return null;
                        }

                        const currentValue = getRowCustomColumnValue(d, customColumnId);
                        const options = customColumn.options || [];

                        if (customColumn.inputType === 'dropdown') {
                          return (
                            <TableCell
                              key={`custom-column-cell-${customColumnId}-${dataIdx}`}
                              className={cn('p-0 w-[170px] min-w-[170px] max-w-[170px]')}
                            >
                              <div className="min-h-[70px] px-[6px] py-0 flex items-center">
                                <DropdownMenu modal={false}>
                                  <DropdownMenuTrigger asChild>
                                    <button
                                      type="button"
                                      className="inline-flex h-9 w-full items-center justify-start gap-1 px-0 text-left text-sm text-[#0f172a] bg-transparent border-0 shadow-none hover:bg-[#f8fafc] focus-visible:outline-none focus-visible:ring-0"
                                    >
                                      <span
                                        className={cn(
                                          'truncate',
                                          currentValue ? 'text-foreground' : 'text-muted-foreground'
                                        )}
                                      >
                                        {currentValue || 'Select'}
                                      </span>
                                      <LucideChevronDown className="h-4 w-4 shrink-0 text-slate-500" />
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent
                                    align="start"
                                    sideOffset={4}
                                    className="min-w-[180px] max-w-[260px] max-h-[340px] overflow-y-auto rounded-[8px] border border-[#d5dde5] bg-white p-1 shadow-[0_10px_28px_rgba(15,23,42,0.15)] z-[999]"
                                  >
                                    {currentValue ? (
                                      <>
                                        <DropdownMenuItem
                                          className="text-muted-foreground italic cursor-pointer"
                                          onSelect={() => {
                                            void persistCustomColumnValue(d, customColumnId, '');
                                          }}
                                        >
                                          Clear selection
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                      </>
                                    ) : null}
                                    {options.map((option) => (
                                      <DropdownMenuItem
                                        key={`${customColumnId}-${option}`}
                                        className="cursor-pointer text-gray-500"
                                        onSelect={() => {
                                          if (option === currentValue) return;
                                          void persistCustomColumnValue(d, customColumnId, option);
                                        }}
                                      >
                                        {option}
                                      </DropdownMenuItem>
                                    ))}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </TableCell>
                          );
                        }

                        return (
                          <TableCell
                            key={`custom-column-cell-${customColumnId}-${dataIdx}`}
                            className={cn('p-0 w-[170px] min-w-[170px] max-w-[170px]')}
                          >
                            <div className="min-h-[70px] w-full min-w-0 px-[6px] py-0 flex items-center overflow-hidden">
                              <div className="w-full min-w-0 max-w-[158px]">
                                <CustomColumnTextCell
                                  value={currentValue}
                                  onCommit={async (nextValue) => {
                                    await persistCustomColumnValue(d, customColumnId, nextValue);
                                  }}
                                />
                              </div>
                            </div>
                          </TableCell>
                        );
                      })}
                      {showColumn('orderedDate') && (
                        <TableCell
                          className={cn(
                            'p-0 w-[140px]',
                            'md:sticky md:z-[50]',
                            showColumn('receivedDate') ? 'md:right-[195px]' : 'md:right-[55px]'
                          )}
                        >
                          <div className="min-h-[70px] px-[6px] py-0 align-middle md:shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.08)] flex items-center">
                            <div className="space-y-1">
                              <div className="inline-flex items-center gap-1 group [&:hover_.orderedDate]:opacity-100">
                                {d?.componentType === 20 ? (
                                  <span>—</span>
                                ) : // d?.order?.orderType === 'purchased-goods' ||
                                purchaserDisplayName() === 'Workroom' ? (
                                  <>
                                    <Popover>
                                      <PopoverTrigger asChild>
                                        <button
                                          className="text-[14px] transition-colors hover:text-primary focus:outline-none text-foreground"
                                          type="button"
                                        >
                                          {orderedDateValue
                                            ? moment(new Date(orderedDateValue)).format('MM/DD/YY')
                                            : `MM/DD/YY`}
                                        </button>
                                      </PopoverTrigger>
                                      <PopoverContent align={'start'} className="p-0 min-w-max">
                                        <Calendar
                                          selected={
                                            orderedDateValue
                                              ? new Date(orderedDateValue)
                                              : undefined
                                          }
                                          mode="single"
                                          className="rounded-md border-0 p-[12px]"
                                          onSelect={(v) =>
                                            handleChooseDate(v, 'orderedDate', d, dataIdx)
                                          }
                                        />
                                      </PopoverContent>
                                    </Popover>

                                    {orderedDateValue && (
                                      <Button
                                        type="button"
                                        variant={'x_btn'}
                                        className="orderedDate p-0 max-h-max opacity-0"
                                        onClick={() =>
                                          void handleChooseDate(
                                            undefined,
                                            'orderedDate',
                                            d,
                                            dataIdx
                                          )
                                        }
                                      >
                                        <LucideX className="lucide lucide-x h-3 w-3" />
                                      </Button>
                                    )}
                                  </>
                                ) : (
                                  <span>COM</span>
                                )}
                              </div>

                              <div className="flex items-center">
                                {d?.componentType === 20 ? (
                                  <span>—</span>
                                ) : !props.canOpenPO ? (
                                  <span className="wrap-text text-left hover:text-primary transition">
                                    {poNumber ? `#${poNumber}` : '—'}
                                  </span>
                                ) : hasPoGenerated ? (
                                  <button
                                    className="wrap-text text-left hover:text-primary transition"
                                    disabled={!props.canOpenPO}
                                    onClick={() => handleOpenLinkedPO(poNumber)}
                                    data-qa={`po-number-link-${dataIdx}`}
                                  >
                                    #{poNumber}
                                  </button>
                                ) : rowIndex === dataIdx ? (
                                  <>
                                    #
                                    <Input
                                      id={`po_number_input_row_${dataIdx}`}
                                      onChange={(e) => handleChangePoNumber(e)}
                                      onBlur={() => handleOnBlurPoNumber(d, dataIdx)}
                                      className="text-[14px] min-h-[25px] max-h-[25px] p-[2px] text-left text-foreground"
                                      value={poNumberState}
                                    />
                                  </>
                                ) : (
                                  <>
                                    <span
                                      onClick={() => {
                                        setRowIndex(dataIdx);
                                        setPoNumberState(poNumber);
                                      }}
                                      className="cursor-pointer whitespace-normal break-words break-all text-sm transition-colors hover:text-primary text-left text-foreground"
                                    >
                                      #{poNumber ? poNumber : '_'}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                      )}
                      {showColumn('receivedDate') && (
                        <TableCell
                          className={cn('p-0 w-[140px]', 'md:sticky md:right-[55px] md:z-[50]')}
                        >
                          <div
                            className={cn(
                              'min-h-[70px] px-[6px] py-0 flex items-center',
                              !showColumn('orderedDate')
                                ? 'md:shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.08)]'
                                : ''
                            )}
                          >
                            <div className="space-y-1">
                              <div className="flex items-center gap-1 group [&:hover_.receivedDate]:opacity-100">
                                {d?.componentType === 20 ? (
                                  <span>—</span>
                                ) : (
                                  <>
                                    <Popover>
                                      <PopoverTrigger asChild>
                                        <button
                                          className="text-[14px] transition-colors hover:text-primary focus:outline-none text-foreground"
                                          type="button"
                                        >
                                          {receivedDateValue
                                            ? moment(new Date(receivedDateValue)).format('MM/DD/YY')
                                            : `MM/DD/YY`}
                                        </button>
                                      </PopoverTrigger>
                                      <PopoverContent align={'start'} className="p-0 min-w-max">
                                        <Calendar
                                          selected={
                                            receivedDateValue
                                              ? new Date(receivedDateValue)
                                              : undefined
                                          }
                                          mode="single"
                                          className="rounded-md border-0 p-[12px]"
                                          onSelect={(v) =>
                                            handleChooseDate(v, 'receivedDate', d, dataIdx)
                                          }
                                        />
                                      </PopoverContent>
                                    </Popover>

                                    {receivedDateValue && (
                                      <Button
                                        type="button"
                                        variant={'x_btn'}
                                        className="receivedDate p-0 max-h-max opacity-0"
                                        onClick={() =>
                                          void handleChooseDate(
                                            undefined,
                                            'receivedDate',
                                            d,
                                            dataIdx
                                          )
                                        }
                                      >
                                        <LucideX className="lucide lucide-x h-3 w-3" />
                                      </Button>
                                    )}
                                  </>
                                )}
                              </div>

                              <div className="w-full flex items-center flex-wrap gap-1 group [&:hover_.etaDate]:opacity-100">
                                {d?.componentType === 20 ? (
                                  <span>—</span>
                                ) : (
                                  <>
                                    <span className="text-[14px] text-teal shrink-0">ETA</span>

                                    <Popover>
                                      <PopoverTrigger asChild>
                                        <button
                                          className="text-[14px] transition-colors hover:text-primary focus:outline-none text-foreground"
                                          type="button"
                                        >
                                          {etaDateValue
                                            ? moment(new Date(etaDateValue)).format('MM/DD/YY')
                                            : `MM/DD/YY`}
                                        </button>
                                      </PopoverTrigger>
                                      <PopoverContent align={'start'} className="p-0 min-w-max">
                                        <Calendar
                                          selected={
                                            etaDateValue ? new Date(etaDateValue) : undefined
                                          }
                                          mode="single"
                                          className="rounded-md border-0 p-[12px]"
                                          onSelect={(v) => handleChooseDate(v, 'eta', d, dataIdx)}
                                        />
                                      </PopoverContent>
                                    </Popover>
                                    {etaDateValue && (
                                      <Button
                                        type="button"
                                        variant={'x_btn'}
                                        className="etaDate p-0 max-h-max opacity-0"
                                        onClick={() =>
                                          void handleChooseDate(undefined, 'eta', d, dataIdx)
                                        }
                                      >
                                        <LucideX className="lucide lucide-x h-3 w-3" />
                                      </Button>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                      )}
                      <TableCell></TableCell>
                    </TableRow>
                  );
                })}
              {/* {sortedData?.length === 0 && ( */}
              {data?.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={100}
                    className="h-[100px] text-center text-sm text-muted-foreground"
                  >
                    No results.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <div
            className="mr-2.5 flex h-full min-h-[52px] items-center justify-end rounded-b-[0.28rem] border border-[#e2e2e2] px-[33px] py-2.5 pr-[65px]"
            data-qa="overview-pagination"
          >
            <div className="flex h-8 items-center gap-[27px]">
              <div className="flex items-center justify-center">
                <span className="font-medium">Rows per page</span>
              </div>
              <div className="flex items-center justify-center">
                <select
                  onChange={(e) => handlePageLimitChange(e)}
                  value={pageLimit}
                  className={paginationSelectClassName}
                  data-qa="overview-pagination-select"
                >
                  <option value={10} data-qa="overview-pagination-option-10">
                    10
                  </option>
                  <option value={25} data-qa="overview-pagination-option-25">
                    25
                  </option>
                  <option value={50} data-qa="overview-pagination-option-50">
                    50
                  </option>
                  <option value={100} data-qa="overview-pagination-option-100">
                    100
                  </option>
                </select>
              </div>
              <div className="flex items-center justify-center">
                <span>
                  {pageNumber > 1 ? pageLimit * pageNumber - (pageLimit - 1) : 1}-
                  {ordersLength >= pageLimit * pageNumber ? pageLimit * pageNumber : ordersLength}{' '}
                  of {ordersLength}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  disabled={pageNumber <= 1 || loading}
                  onClick={() => handleFirstPage()}
                  className="h-8 w-8"
                  data-qa="overview-pagination-first"
                >
                  <PaginationEndIcon
                    className={cn(
                      'rotate-180',
                      pageNumber <= 1 ? 'opacity-40 cursor-auto' : 'cursor-pointer'
                    )}
                  />
                </Button>

                <Button
                  variant="ghost"
                  size="icon"
                  disabled={pageNumber <= 1 || loading}
                  onClick={() => handlePrevPage()}
                  className="h-8 w-8"
                  data-qa="overview-pagination-prev"
                >
                  <PaginationNextIcon
                    className={cn(
                      'rotate-180',
                      pageNumber <= 1 ? 'opacity-40 cursor-auto' : 'cursor-pointer'
                    )}
                  />
                </Button>

                <Button
                  variant="ghost"
                  size="icon"
                  disabled={pageNumber >= totalPages || loading}
                  onClick={() => handleNextPage()}
                  className="h-8 w-8"
                  data-qa="overview-pagination-next"
                >
                  <PaginationNextIcon
                    className={cn(
                      pageNumber >= totalPages ? 'opacity-40 cursor-auto' : 'cursor-pointer'
                    )}
                  />
                </Button>

                <Button
                  variant="ghost"
                  size="icon"
                  disabled={pageNumber >= totalPages || loading}
                  onClick={() => handleLastPage()}
                  className="h-8 w-8"
                  data-qa="overview-pagination-last"
                >
                  <PaginationEndIcon
                    className={cn(
                      pageNumber >= totalPages ? 'opacity-40 cursor-auto' : 'cursor-pointer'
                    )}
                  />
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Dialog
        open={isCreatePoModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleCloseCreatePoModal();
            return;
          }
          setIsCreatePoModalOpen(true);
        }}
      >
        <DialogContent className="max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="m-0 text-[24px]">Enter PO number</DialogTitle>
          </DialogHeader>

          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              void handleSaveBatchPoNumber();
            }}
          >
            <Input
              autoFocus
              value={batchPoNumber}
              onChange={(event) => setBatchPoNumber(event.target.value)}
              placeholder="PO number"
              className="text-[14px]"
              data-qa="client-ordering-receiving-batch-po-input"
            />

            <DialogFooter className="flex-row justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseCreatePoModal}
                disabled={isSavingBatchPo}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSavingBatchPo || !batchPoNumber.trim()}>
                {isSavingBatchPo ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <StandardItemModal
        isModalOpen={isMyShopModalOpen}
        categories={sheetCategories}
        suppliers={sheetSuppliers}
        itemId={myShopItemId}
        activeItemClassId={myShopItemClassId}
        onModalClose={handleMyShopModalClose}
        fromCalculator={true}
        useWideSheet
        canEditMyShop={account?.isOwner || account?.isAdmin || account?.canSeePricing}
      />

      <LoadingModal
        message="Creating work order..."
        isModalOpen={isLoadingOpen}
        onModalClose={toggleLoadingModal}
      />

      <SupplierModal
        key={`${supplierModal?.item?.id || 'new'}_key`}
        accountId={accountId}
        isModalOpen={supplierModal.active}
        onModalClose={handleCloseSupplierModal}
        onSave={handleSupplierChanged}
        resetOnClose
        initialValues={supplierModal?.item}
        // setFocusFirstField={(fn) => (setFocusFirstFieldOnSupplierModal = fn)}
      />
    </div>
  );
};

export default ClientOrderingAndReceiving;
